/**
 * fuarKuralService.js — Fuar Kural Sistemi (yükseklik tavanları + fuar kuralları)
 *
 * Base URL: {JAVA_BACKEND_API_URL}/fuar
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 * Kaynak: docs/guides/FE-fuar-kural-sistemi-guide.md (2026-08-24 · rev. 2026-08-28/29)
 * Canlı teyitler: util/knowledgebase/fuar-kural-sistemi.md
 *
 * Bu dosya, organizatörün bir fuara yazdığı kuralları okur/yazar. Dekoratörün
 * beyanı (POST /projects/{id}/declare) bu kurallara karşı yargılanır; beyan
 * tarafındaki uçlar projectService.js'te (declareProject/validateDeclaration).
 * Belge kuralları ayrı bir tabloda ve ayrı bir serviste: belgeKuralService.js.
 *
 * ⚠️ ÜÇ KURAL — sayfalarda tekrar tekrar karşınıza çıkacak:
 *
 * 1. HİÇBİR ŞEY YAPILANDIRILMAMIŞ = HİÇBİR ŞEY ZORLANMIYOR. Bütün varsayılanlar
 *    müsamahakâr. `configured: false` bir HATA DEĞİL — bugün canlıdaki her fuar
 *    orada. "Kural yok" / "sınırsız" diye kırmızı bir şey basmayın.
 * 2. `null` ≠ `false` ≠ `0`. null = sorulmadı/atanmadı/miras al. `yasak: false`
 *    bir üst tipin koyduğu yasağı KALDIRIR — yanlışlıkla false göndermek sessizce
 *    yasağı kaldırır. Tri-state alanlarda `''` (miras) ile `false`'u ayrı tutun.
 * 3. EN SPESİFİK KAZANIR ve DEĞERİN TAMAMINI VERİR — birleştirme (merge) yok.
 *
 * ═══ 2026-09-15 — YÜKSEKLİK LİMİTLERİ (backlog #23, 15_9_yukseklik_limitleri_salon.sql) ═══
 *
 * "Tavan" → "limit". Fuar alanı geneli sınır (FAIRGROUND) KALDIRILDI; her salonun fuar
 * alanında kendi varsayılanı var ve fuardaki salon sınırı KAT sayısına göre ikiye ayrıldı:
 *
 *   fuar_alani_salon.ceiling_height     salonun o fuar alanındaki FİZİKSEL tavanı  (Salonlar listesinde)
 *   fuar_alani_salon.allowed_h_max      salonun VARSAYILAN izin verilen yüksekliği  (Salonlar listesinde)
 *   fuar.fuar_h_max                     bu fuarın kendi limiti (Politika Limiti)
 *   salon_to_fuar.salon_h_max_tek_kat   bu fuarda bu salon, tek katlı standlar
 *   salon_to_fuar.salon_h_max_cift_kat  bu fuarda bu salon, çift katlı standlar
 *
 * ÇÖZÜM: kat sayısı standın tipinden gelir (Tek Katlı / Çift Katlı düğümü). O kata ait
 * salon limiti kazanır, yoksa fuar limiti, yoksa salonun varsayılanı. Kat düğümü olmayan
 * bir stand tipi salon limitlerini ATLAR — yalnızca fuar limiti. Sonra salonun varsayılanı
 * ve fiziksel tavan sonucu KIRPAR. `null` "kayıt yok"tur, "sınır yok" değil.
 *
 * Kaynaklar: HALL / FAIR / HALL_DEFAULT / CEILING / NONE (+ STAND_TYPE). FAIRGROUND yok.
 *
 * KAPSAMA (iki yönden zorlanır):
 *   salon_h_max_*_kat <= fuar_h_max, <= allowed_h_max, <= ceiling_height;  allowed_h_max <= ceiling_height
 *   · yukarı doğru aşmak → 400 ("önce üstünü yükseltin")
 *   · altına inmek       → 409 + `data` altta kalan kayıtları ADLANDIRIR (err.conflicts)
 *
 * ═══ 2026-09-18 — BACKLOG #48: FUAR LİMİTİ ARTIK SADECE DOLDURUYOR ═══
 *
 * WAS: yukarıdaki `salon_h_max_*_kat <= fuar_h_max` kapsaması iki yönde de zorlanıyordu
 * (setSalonHMax yukarı, setFuarHMax aşağı). KALDIRILDI — fuar limiti artık salon değerlerini
 * hiçbir yönde ENGELLEMİYOR, yalnızca applyFuarHMaxToHalls ile TOPLU DOLDURUYOR. Salonun kendi
 * fuar alanı sınırı (allowed_h_max / ceiling_height) hâlâ geçerli: toplu uygulama o sınırı aşan
 * bir salonu ATLAR (skippedHalls), bloklamaz. setFuarHMax/setSalonHMax'ın eski karşılıklı kontrol
 * kodu backend'de YORUM SATIRI olarak duruyor, silinmedi.
 *
 * Fuar alanı ve salon yönetimi (tavan + varsayılan) fuarAlaniSalonService.js'te.
 *
 * İkinci, BAĞIMSIZ eksen hâlâ duruyor: fuar_stand_tipi_kural.hMax (stand tipi
 * ağacında miras alır). Uygulanan sınır İKİSİNİN DÜŞÜĞÜ — konum fiziksel,
 * stand tipi politika, fiziksel olan her zaman bağlar.
 *
 * Geri gelmeyenler: `fuar_kural.serbest_kw_m2` (yerine serbest kW KADEMELERİ) ve
 * `lockReason: "HEIGHT_MISMATCH"`.
 *
 * `source: "NONE"` = "hiçbir yerde kayıt yok, yargılayamıyorum" — "sınırsız" DEĞİL.
 *
 * ═══ 2026-09-17 — FUAR KURALLARI SAYFASI (backlog #52–#57) ═══
 *
 *  · #52 Serbest kW DÖRT mod: NONE / FLAT / BLOCK / BANDS. BLOCK = "her N m² için K kW",
 *    yalnızca TAM bloklar (5 m² başına 2 kW → 12 m² = 4 kW). Tek mod; backend karışımı 400'ler.
 *  · #53 Cam/boya sabit alanlardan çıktı → yönetilebilir "kullanım kuralları" kataloğu
 *    (kod CAM/BOYA + yenileri). GET /kural `kullanimKurallari[]` döner; PUT'ta o dizi
 *    OTORİTER — gönderilmeyen kod varsayılana döner. boyaX / camX alanları yalnızca geriye
 *    uyumluluk için geliyor; bu dosyanın ekranları artık okumuyor.
 *  · #54 Kimlik doğrulama artık fuar kuralı DEĞİL — her fuarda zorunlu (WAS: "Özel İzin muaf" — #474'ten beri Özel İzin kimliğin yerine geçmez).
 *    GET hep `true` döner, PUT alanı yok sayar; biz de göndermiyoruz.
 *  · #55/#57 Stand tipi kuralları fuar 0'dan (global) CANLI MİRAS alır, fuar satırları alan
 *    alan ezer. Okuma: GET /kural/stand-tipi/effective (ağaç). Yazma değişmedi.
 *    applyGlobalDefaultsToFuar stand tipi kurallarını ARTIK KOPYALAMAZ.
 *
 * ⚠️ OKUMA/YAZMA ZARF ASİMETRİSİ (guide + canlı teyit): her GET ÇIPLAK obje/dizi
 *    döner, her PUT/DELETE `{message, result, data}` zarfı döner ve `data` zaten
 *    yazma SONRASI durumdur — kaydettikten sonra tekrar GET atmak boşuna. unwrap()
 *    ikisini de normalize eder.
 *
 * ⚠️ AUTH: FuarController'da lokal bypass YOK (guide 2026-08-29 teyidi). Buradaki
 *    her uç gerçek bir oturum çifti ister (X-user-id + X-token) ve yoksa 401
 *    "Invalid login token" döner — CORS değil, hata değil. /projects/belge-kural*
 *    tarafı bypass'lı; bu yüzden belge kuralları ayrı serviste.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { currentLanguageId } from '../i18n';
// Yalnızca mock stand tipi ağacını yürümek için (projectService bu dosyayı import etmiyor — döngü yok).
import { getStandTypes } from './projectService';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/fuar`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
// projectService.js ile aynı sözleşme: sayfalar canlı fuarlar için { forceLive: true }
// geçer (bkz. LIVE_TEST_FUAR_IDS köprüsü), mock fuarlar bu dosyadaki veriyi görür.
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

/** Mock dalında backend'in 400/409'unu taklit eder — sayfalar aynı hata yolunu görsün. */
function _mockError(status, message) {
  const err = new Error(message);
  err.status = status;
  return err;
}

// ─────────────────────────────────────────────────────────────────────────────
// SABİTLER
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Yükseklik limitinin hangi katmandan geldiği (KuralIhlaliDTO.source ile aynı sözlük).
 * ⚠️ 2026-09-15: FAIRGROUND kaldırıldı, HALL_DEFAULT (salonun fuar alanındaki varsayılanı)
 *    geldi. FAIRGROUND anahtarı eski kayıtlı ihlalleri okuyabilmek için duruyor.
 */
export const HMAX_SOURCE = {
  HALL: 'HALL',
  FAIR: 'FAIR',
  HALL_DEFAULT: 'HALL_DEFAULT',
  FAIRGROUND: 'FAIRGROUND', // kaldırıldı — yalnızca eski veriyi etiketlemek için
  CEILING: 'CEILING',
  STAND_TYPE: 'STAND_TYPE',
  FUAR_KURAL: 'FUAR_KURAL',
  NONE: 'NONE',
};

export const HMAX_SOURCE_LABELS = {
  get HALL() { return i18n.t("services:fuarKuralService.salonLimiti"); },
  get FAIR() { return i18n.t("services:fuarKuralService.fuarLimiti"); },
  get HALL_DEFAULT() { return i18n.t("services:fuarKuralService.salonunIzinVerilenYuksekligi"); },
  get FAIRGROUND() { return i18n.t("services:fuarKuralService.fuarAlaniLimitiKaldirildi"); },
  // Bilinçli olarak "salonun tavanı" — dekoratör "birileri böyle karar verdi" değil
  // "salon bu kadar yüksek" okusun diye (guide, Part 4).
  get CEILING() { return i18n.t("services:fuarKuralService.salonunFizikselTavani"); },
  get STAND_TYPE() { return i18n.t("services:fuarKuralService.standTipi"); },
  get FUAR_KURAL() { return i18n.t("services:fuarKuralService.fuarKurali"); },
  get NONE() { return i18n.t("services:fuarKuralService.kayitYok"); },
};

/** Kat tipleri — backend KatTipiConstant ile aynı. null = stand tipinde kat düğümü yok. */
export const KAT_TIPI = { TEK_KAT: 'TEK_KAT', CIFT_KAT: 'CIFT_KAT' };
export const KAT_TIPI_LABELS = { get TEK_KAT() { return i18n.t("services:fuarKuralService.tekKatli"); }, get CIFT_KAT() { return i18n.t("services:fuarKuralService.ciftKatli"); } };

/** Politika seviyeleri DARDAN GENİŞE — çözüm sırası tam olarak budur. */
export const HMAX_POLICY_LEVELS = [HMAX_SOURCE.HALL, HMAX_SOURCE.FAIR, HMAX_SOURCE.HALL_DEFAULT];

/**
 * Backend'deki FuarService.SalonHeight.resolve'un aynası (mock ve ekran önizlemesi için) —
 * iki taraf aynı cevabı üretmek zorunda.
 *
 * O kata ait salon limiti → fuar limiti → salonun varsayılanı; sonra varsayılan ve fiziksel
 * tavan KIRPAR. katTipi null ise salon limitleri atlanır.
 */
export function resolveHallHMax({
  salonHMaxTekKat = null, salonHMaxCiftKat = null, katTipi = null,
  fuarHMax = null, allowedHMax = null, ceilingHeight = null,
} = {}) {
  const hall = katTipi === KAT_TIPI.TEK_KAT ? salonHMaxTekKat
    : katTipi === KAT_TIPI.CIFT_KAT ? salonHMaxCiftKat : null;
  let policy = null;
  if (hall != null) policy = { v: Number(hall), source: HMAX_SOURCE.HALL };
  else if (fuarHMax != null) policy = { v: Number(fuarHMax), source: HMAX_SOURCE.FAIR };
  else if (allowedHMax != null) policy = { v: Number(allowedHMax), source: HMAX_SOURCE.HALL_DEFAULT };

  let hMax = policy?.v ?? null;
  let source = policy?.source ?? null;
  if (allowedHMax != null && hMax != null && Number(allowedHMax) < hMax) {
    hMax = Number(allowedHMax); source = HMAX_SOURCE.HALL_DEFAULT;
  }
  if (ceilingHeight != null && (hMax == null || Number(ceilingHeight) < hMax)) {
    hMax = Number(ceilingHeight); source = HMAX_SOURCE.CEILING;
  }
  return {
    hMax,
    source: hMax == null ? HMAX_SOURCE.NONE : source,
    clampedByCeiling: source === HMAX_SOURCE.CEILING && policy != null,
    clampedByDefault: source === HMAX_SOURCE.HALL_DEFAULT && policy != null
      && policy.source !== HMAX_SOURCE.HALL_DEFAULT,
  };
}

/**
 * Bir salon limitinin aşamayacağı en düşük üst sınır ve adı — kaydetmeden önce uyarmak
 * için. Backend fiziksel tavan → varsayılan denetler.
 *
 * WAS (backlog #48'e kadar): `fuarHMax` de bir aday sınırdı — backend'in setSalonKatHMax'ı
 * salon değerinin fuar limitini aşmasını engelliyordu. #48 o kontrolü KALDIRDI (yorum satırı
 * olarak duruyor, FuarService.checkHallLimit); salonun kendi fuar alanı sınırı (tavan/varsayılan)
 * hâlâ geçerli, ama fuar limiti artık yalnızca applyFuarHMaxToHalls ile TOPLU dolduruyor.
 */
export function hallLimitCeiling({ allowedHMax = null, ceilingHeight = null } = {}) {
  const candidates = [
    ceilingHeight != null && { value: Number(ceilingHeight), source: HMAX_SOURCE.CEILING },
    allowedHMax != null && { value: Number(allowedHMax), source: HMAX_SOURCE.HALL_DEFAULT },
  ].filter(Boolean);
  if (candidates.length === 0) return { value: null, source: HMAX_SOURCE.NONE };
  return candidates.reduce((a, b) => (b.value < a.value ? b : a));
}

/** İhlal kodları — asla dekoratöre gösterilmez, sadece ikon/renk seçimi için. */
export const VIOLATION_CODE = {
  STAND_TYPE_FORBIDDEN: 'STAND_TYPE_FORBIDDEN',
  HEIGHT_ABOVE_LIMIT: 'HEIGHT_ABOVE_LIMIT',
  AREA_REQUIRED: 'AREA_REQUIRED',
  AREA_BELOW_MINIMUM: 'AREA_BELOW_MINIMUM',
  AREA_ABOVE_MAXIMUM: 'AREA_ABOVE_MAXIMUM',
  GLASS_NOT_ALLOWED: 'GLASS_NOT_ALLOWED',
  PAINT_NOT_ALLOWED: 'PAINT_NOT_ALLOWED',
  FREE_KW_EXCEEDED: 'FREE_KW_EXCEEDED',
  // 2026-09-17 (#53): CAM/BOYA dışındaki her kullanım kuralı; field = "declaredKullanimlar.<KOD>".
  USAGE_NOT_ALLOWED: 'USAGE_NOT_ALLOWED',
};

/**
 * ⚠️ 2026-08-28: ÜÇÜNCÜ bir severity var.
 *  ERROR     → reddedildi, hiçbir şey kaydedilmedi (ya da kaydedildiyse kilitli)
 *  WARNING   → kabul edildi ve kaydedildi, sadece bilgi (bugün yalnız FREE_KW_EXCEEDED)
 *  PERMITTED → ONAYLI BİR ÖZEL İZNİN temizlediği bir ERROR. İhlal GERÇEK ve listede
 *              kalır; sadece artık projeyi kilitlemiyor. YEŞİL/"tamam" diye basmayın:
 *              5,00 m'lik salondaki 5,50 m'lik stand, "yasal görünen bir sayı" değil
 *              "birinin imzaladığı bir istisna" olarak okunmalı.
 */
export const VIOLATION_SEVERITY = { ERROR: 'ERROR', WARNING: 'WARNING', PERMITTED: 'PERMITTED' };

/**
 * KuralIhlaliDTO.field → beyan formundaki input anahtarı. Bir ihlali doğru inputun
 * altına iliştirmek için; eşleşmeyen alanlar formun tepesindeki toplu listeye düşer.
 */
// Sabit alanlar. Katalogdaki diğer kullanım kuralları "declaredKullanimlar.<KOD>" alanıyla gelir
// (bkz. VIOLATION_FIELD_KULLANIM_PREFIX) — bu listede olmayan bir alan yok sayılmamalı.
export const VIOLATION_FIELDS = [
  'standTypeId', 'declaredHeightNumber', 'declaredAreaM2',
  'declaredKw', 'declaredGlass', 'declaredPaint',
];
export const VIOLATION_FIELD_KULLANIM_PREFIX = 'declaredKullanimlar.';

/**
 * Serbest kW mekanizması. Bir fuar bunlardan EN FAZLA BİRİNİ kullanır — backend karışımı
 * her yönden reddeder (sabit + blok → 400, blok/sabit varken kademe → 400). Mod SAKLANMAZ,
 * veriden TÜRETİLİR: editörü buna göre çizin, null'lara bakarak tahmin etmeyin.
 * ⚠️ 2026-09-17 (#52): BLOCK eklendi — "her N m² için K kW", yalnızca TAM bloklar.
 * Anahtar sırası ekrandaki düğme sırasıdır (Yok / Sabit / Matematiksel / Kademeli).
 */
export const SERBEST_KW_MODE = { NONE: 'NONE', FLAT: 'FLAT', BLOCK: 'BLOCK', BANDS: 'BANDS' };

export const SERBEST_KW_MODE_LABELS = {
  get NONE() { return i18n.t("services:fuarKuralService.yokSerbestElektrikHakkiTanimlanmamis"); },
  get FLAT() { return i18n.t("services:fuarKuralService.sabitHerStandAyniHakki"); },
  get BLOCK() { return i18n.t("services:fuarKuralService.matematikselHerTamMBlogu"); },
  get BANDS() { return i18n.t("services:fuarKuralService.kademeliMYeGoreDegisir"); },
};

/** Düğme grubundaki kısa adlar. */
export const SERBEST_KW_MODE_SHORT_LABELS = {
  NONE: 'Yok', FLAT: 'Sabit', BLOCK: 'Matematiksel', BANDS: 'Kademeli',
};

/**
 * fuar_kural varsayılanları — satır yoksa backend de tam olarak bunları döner.
 * PUT /kural TAM DEĞİŞTİRME (full replace) olduğu için form her zaman bu şeklin
 * tamamını göndermeli; eksik bırakılan boolean eski değerine değil VARSAYILANA döner.
 * (2026-08-28'de kimlikDogrulamaZorunlu bu listeye katılmıştı; 2026-09-17'de (#54)
 *  kural olmaktan çıktı — backend alanı yok sayıyor.)
 * ⚠️ 2026-09-04: m2Zorunlu ARTIK BİR AYAR DEĞİL, SABİT. Her iki kural ekranından da
 *    anahtarı kaldırıldı (bkz. M2_ZORUNLU_SABIT) — organizatöre sorulmuyor, her
 *    fuarda açık. Varsayılan `true` ve `saveFuarKural` gönderilen değeri EZİYOR;
 *    yani kayıtta `false` duran eski bir fuar da bir sonraki kaydetmede düzelir.
 */
export const M2_ZORUNLU_SABIT = true;

/**
 * ⚠️ 2026-09-17: boyaSorulsun / camSorulsun / boyaIzinli / camIzinli ve
 *    kimlikDogrulamaZorunlu bu şekilden ÇIKTI. Cam/boya artık `kullanimKurallari`
 *    dizisinde (#53), kimlik doğrulama artık bir kural değil (#54). PUT gövdesi:
 *    { serbestKw, serbestKwBlokKw, serbestKwBlokM2, m2Zorunlu, kullanimKurallari }.
 */
export const KURAL_DEFAULTS = {
  serbestKw: null,
  serbestKwBlokKw: null,
  serbestKwBlokM2: null,
  m2Zorunlu: M2_ZORUNLU_SABIT,
  kullanimKurallari: [],
};

/** Yapılandırılmamış bir kullanım kuralının fuardaki değerleri (backend ile aynı). */
export const KULLANIM_KURALI_DEFAULTS = { izinli: true, sorulsun: false };

/** Seed edilmiş iki katalog kodu — geriye uyumlu askGlass/askPaint türetmek için. */
export const KULLANIM_KURALI_KOD = { CAM: 'CAM', BOYA: 'BOYA' };

/**
 * Serbest kW modunu VERİDEN türetir — `serbestKwMode` backend'de saklanmıyor (GET
 * artık türetilmişini döndürüyor; gelmezse bu kullanılır). Birden çoğu doluysa backend
 * zaten yazmayı reddeder; BANDS'i öne alıyoruz çünkü kademe tablosu ayrı tabloda yaşar
 * ve `configured:false` iken bile gelir. Blok çiftinden BİRİ bile doluysa BLOCK — yarım
 * kalmış bir blok editörü "Yok" diye çizilirse sayı sessizce kaybolur.
 */
export function deriveSerbestKwMode(kural) {
  if (Array.isArray(kural?.serbestKwKademeleri) && kural.serbestKwKademeleri.length > 0) {
    return SERBEST_KW_MODE.BANDS;
  }
  if (kural?.serbestKwBlokKw != null || kural?.serbestKwBlokM2 != null) return SERBEST_KW_MODE.BLOCK;
  if (kural?.serbestKw != null) return SERBEST_KW_MODE.FLAT;
  return SERBEST_KW_MODE.NONE;
}

/**
 * BLOCK hakkı: floor(alan / blokM2) × blokKw — yalnızca TAM bloklar sayılır.
 * m² yoksa ya da blok tanımı eksikse `null` ("yargılayamıyorum"), 0 değil.
 * Küçük epsilon: 0,3 / 0,1 JS'te 2,999… çıkıyor; backend BigDecimal ile 3 buluyor.
 */
export function resolveBlockKw(blokKw, blokM2, m2) {
  if (m2 === '' || m2 == null || blokKw === '' || blokKw == null || blokM2 === '' || blokM2 == null) return null;
  const area = Number(m2);
  const size = Number(blokM2);
  const kw = Number(blokKw);
  if (!Number.isFinite(area) || !Number.isFinite(size) || !Number.isFinite(kw) || size <= 0) return null;
  const blocks = Math.floor(area / size + 1e-9);
  return Math.round(Math.max(0, blocks) * kw * 1000) / 1000;
}

/** Blok çiftini kaydetmeden önce denetler — backend'in 400 kurallarının aynası. */
export function validateSerbestKwBlok(blokKw, blokM2) {
  const kw = blokKw === '' || blokKw == null ? null : Number(blokKw);
  const size = blokM2 === '' || blokM2 == null ? null : Number(blokM2);
  const errors = [];
  if (kw == null || size == null) errors.push('Matematiksel modda m² ve kW ikisi birden zorunlu.');
  if (size != null && !(size > 0)) errors.push(i18n.t("services:fuarKuralService.blokBuyukluguM0Dan"));
  if (kw != null && !(kw >= 0)) errors.push(i18n.t("services:fuarKuralService.blokBasinaKWNegatifOlamaz"));
  return errors;
}

/**
 * Bir standın serbest kW hakkı.
 *
 * ⚠️ 2026-08-26: `serbestKwM2` (m² başına çarpan) KALDIRILDI — zaten hiçbir zaman
 *    bir çarpma değildi. Yerine organizatörün YAYIMLADIĞI bir kademe tablosu geldi:
 *      0–25 m² → 10 kW · 25–40 m² → 15 kW · 40 m²+ → 25 kW
 *
 * Aralıklar YARI AÇIK [minM2, maxM2): minM2 dahil, maxM2 hariç. Yukarıdaki tabloda
 * 25 m²'lik bir stand 15 kW alır ve iki bant boşluksuz/çakışmasız döşenir.
 *
 * ⚠️ HİÇBİR BANDA DÜŞMEYEN stand hak ALMAZ ve UYARI DA ALMAZ — m²'si hiç girilmemiş
 *    stand dahil. Bu "0 kW" değil "yargılayamıyorum" demektir. Üst ucu açık bir bant
 *    (maxM2: null) yoksa 500 m²'lik stand sessizce hiçbir şey alamaz.
 */
export function resolveFreeKw(kural, m2) {
  const mode = deriveSerbestKwMode(kural);
  if (mode === SERBEST_KW_MODE.FLAT) return kural.serbestKw ?? null;
  // 2026-09-17 (#52): tam bloklar — 5 m² başına 2 kW → 12 m² = 4 kW.
  if (mode === SERBEST_KW_MODE.BLOCK) return resolveBlockKw(kural.serbestKwBlokKw, kural.serbestKwBlokM2, m2);
  if (mode !== SERBEST_KW_MODE.BANDS) return null;
  const area = m2 === '' || m2 == null ? null : Number(m2);
  const band = (kural.serbestKwKademeleri || []).find((b) => {
    // İki ucu da null olan bant HER standa uygular — m²'si olmayan bir standın
    // çözülebilmesinin TEK yolu budur.
    if (b.minM2 == null && b.maxM2 == null) return true;
    if (area == null) return false;
    return (b.minM2 == null || area >= Number(b.minM2))
      && (b.maxM2 == null || area < Number(b.maxM2));
  });
  return band ? band.serbestKw ?? null : null;
}

/** Kademe listesinde çakışma/boşluk var mı — kaydetmeden önce kullanıcıya söylemek için. */
export function validateSerbestKwBands(bands) {
  const rows = (bands || []).map((b, i) => ({
    i,
    min: b.minM2 === '' || b.minM2 == null ? null : Number(b.minM2),
    max: b.maxM2 === '' || b.maxM2 == null ? null : Number(b.maxM2),
    kw: b.serbestKw === '' || b.serbestKw == null ? null : Number(b.serbestKw),
  }));
  const errors = [];
  rows.forEach((r) => {
    const label = `${r.min ?? '−'}–${r.max ?? '∞'} m²`;
    if (r.kw == null || r.kw < 0) errors.push(`${label}: serbest kW zorunlu ve negatif olamaz.`);
    if (r.min != null && r.min < 0) errors.push(i18n.t("services:fuarKuralService.altSinirNegatifOlamaz", { label }));
    if (r.max != null && r.max <= 0) errors.push(i18n.t("services:fuarKuralService.ustSinir0DanBuyuk", { label }));
    if (r.min != null && r.max != null && r.min >= r.max) {
      errors.push(i18n.t("services:fuarKuralService.altSinirUstSinirdanKucuk", { label }));
    }
  });
  // Çakışma — backend 400'ü zaten aralığı adlandırıyor ama kaydetmeden söylemek daha iyi.
  for (let a = 0; a < rows.length; a += 1) {
    for (let b = a + 1; b < rows.length; b += 1) {
      const x = rows[a]; const y = rows[b];
      const startsBefore = (x.max == null || y.min == null || Number(y.min) < Number(x.max));
      const endsAfter = (y.max == null || x.min == null || Number(x.min) < Number(y.max));
      if (startsBefore && endsAfter) {
        errors.push(i18n.t("services:fuarKuralService.mIleMCakisiyor", { v: x.min ?? '−', v2: x.max ?? '∞', v3: y.min ?? '−', v4: y.max ?? '∞' }));
      }
    }
  }
  return errors;
}

/** "4,00 m — Ahşap standlar için" gibi atıflı bir limit metni üretir. */
export function describeHeightLimit(eff) {
  if (!eff || eff.heightLimit == null) return null;
  const value = `${Number(eff.heightLimit).toFixed(2).replace('.', ',')} m`;
  if (eff.heightLimitSource === HMAX_SOURCE.STAND_TYPE && eff.heightLimitFromStandTypeName) {
    return i18n.t("services:fuarKuralService.standlarIcin", { value, heightLimitFromStandTypeName: eff.heightLimitFromStandTypeName });
  }
  if (eff.heightLimitSource === HMAX_SOURCE.HALL && eff.hallName) {
    const kat = KAT_TIPI_LABELS[eff.katTipi];
    return i18n.t("services:fuarKuralService.salonuIcinBelirlenenLimiti", { value, hallName: eff.hallName, v: kat ? `${kat.toLowerCase()} stand ` : '' });
  }
  if (eff.heightLimitSource === HMAX_SOURCE.CEILING) {
    const roof = i18n.t("services:fuarKuralService.tavanYuksekligi", { value, v: eff.hallName ? `${eff.hallName} salonunun` : 'Salonun' });
    // clampedByCeiling: politika daha yükseğine izin veriyordu, çatı kesti. Dekoratör
    // "kimse karar vermemiş" ile "binanın kendisi elvermiyor"u ayırt edebilmeli.
    return eff.clampedByCeiling ? i18n.t("services:fuarKuralService.politikaSiniriBurayaKadarIndi", { roof }) : roof;
  }
  if (eff.heightLimitSource === HMAX_SOURCE.FAIR) {
    return i18n.t("services:fuarKuralService.buFuarIcinBelirlenenLimit", { value });
  }
  if (eff.heightLimitSource === HMAX_SOURCE.HALL_DEFAULT) {
    return i18n.t("services:fuarKuralService.izinVerilenYuksekligi", { value, v: eff.hallName ? `${eff.hallName} salonunun` : 'salonun' });
  }
  if (eff.heightLimitSource === HMAX_SOURCE.FAIRGROUND) {
    return i18n.t("services:fuarKuralService.genelSiniri", { value, v: eff.fuarAlaniName ? i18n.t("services:fuarKuralService.fuarAlaninin", { fuarAlaniName: eff.fuarAlaniName }) : i18n.t("services:fuarKuralService.fuarAlaninin2") });
  }
  return i18n.t("services:fuarKuralService.siniri", { value, v: HMAX_SOURCE_LABELS[eff.heightLimitSource] ?? 'Fuar' });
}

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL VARSAYILANLAR
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ FE SÖZLEŞMESİ — backend'de `fuar_kural` için gerçek bir "global" seviyesi YOK.
 *
 * `belge_kural`'ın `fuarId = null` ile ifade ettiği global seviyenin karşılığı
 * `fuar_kural` / `fuar_stand_tipi_kural` tarafında bulunmuyor: her iki tablo da
 * fuar başına tek satır tutuyor ve okuma uçları var olmayan bir fuarı 404'lemiyor
 * (guide: "Neither read 404s on a fair that does not exist" — 200 + permissive
 * defaults). Bunu kullanıp **0 numaralı sahte fuarı GLOBAL YUVA** olarak ayırdık:
 *
 *   GET/PUT /fuar/0/kural            → global skaler varsayılanlar
 *   GET/PUT /fuar/0/kural/serbest-kw → global serbest kW kademeleri
 *   GET/PUT /fuar/0/kural/stand-tipi → global stand tipi kuralları
 *
 * Neden localStorage değil: global ayar TÜM KULLANICILAR için aynı olmalı, tarayıcı
 * başına değil. Neden gerçek bir fuar değil: 0 hiçbir zaman geçerli bir fuar id'si
 * olmuyor, dolayısıyla kimsenin fuarını kirletmiyor ve tek bir SQL ile temizlenebilir
 * (`DELETE FROM fuar_kural WHERE fuar_id = 0`).
 *
 * ⚠️ İKİ FARKLI DAVRANIŞ (2026-09-17, #55/#57):
 *    · Skaler kurallar, kW kademeleri ve kullanım kuralları hâlâ ŞABLON: ancak
 *      `applyGlobalDefaultsToFuar()` ile bir fuara KOPYALANDIĞINDA yürürlüğe girer.
 *    · Stand tipi kuralları artık CANLI MİRAS: backend her fuarın stand tipi zincirinde
 *      önce fuarın satırlarına, alan boşsa fuar 0'ın satırlarına bakar. Fuar 0'daki bir
 *      stand tipi değişikliği açık fuarları da ANINDA etkiler; bu yüzden kopyalanmıyor.
 *
 * Backend gerçek bir global seviye eklerse (belge_kural gibi fuarId=null), tek
 * yapılacak bu sabiti null'a çevirip aşağıdaki sarmalayıcıları silmek.
 * Bkz. docs/BACKEND_TODO.md — "fuar_kural global seviyesi".
 */
export const GLOBAL_KURAL_FUAR_ID = 0;

// ─── Mock veri ────────────────────────────────────────────────────────────────
// fuarId 3 = projectService.js/_mockProjects'in "standart" mock fuarı.
// 2026-09-17: cam/boya bu satırdan _mockKullanimKurali'na taşındı, kimlik alanı kalktı.
const _mockKural = {
  3: {
    serbestKw: 15, serbestKwBlokKw: null, serbestKwBlokM2: null,
    m2Zorunlu: true,
    configured: true,
  },
  // Global şablon — yeni fuarlar bunun kopyasıyla doğar.
  [GLOBAL_KURAL_FUAR_ID]: {
    serbestKw: null, serbestKwBlokKw: null, serbestKwBlokM2: null,
    m2Zorunlu: true,
    configured: true,
  },
};

/**
 * Kullanım kuralı kataloğu (#53) — canlı `GET /fuar/kullanim-kurallari` ile aynı alanlar
 * (fuarSayisi okumada hesaplanır). CAM ve BOYA seed edilmiş iki kayıt.
 */
const _mockKullanimKuraliKatalog = [
  {
    kullanimKuraliId: 1, kod: 'CAM', ad: 'Cam',
    beyanSorusu: 'Standımda cam kullanılacak',
    yasakMesaji: 'Bu fuarda cam kullanımına izin verilmiyor.',
    sira: 1, aktif: true,
  },
  {
    kullanimKuraliId: 2, kod: 'BOYA', ad: 'Boya',
    beyanSorusu: 'Standımda boya kullanılacak',
    yasakMesaji: 'Bu fuarda boya kullanımına izin verilmiyor.',
    sira: 2, aktif: true,
  },
];

// fuarId -> kod -> { izinli, sorulsun }. Satırı olmayan kod = KULLANIM_KURALI_DEFAULTS.
const _mockKullanimKurali = {
  3: { CAM: { izinli: false, sorulsun: true }, BOYA: { izinli: true, sorulsun: true } },
  [GLOBAL_KURAL_FUAR_ID]: { CAM: { izinli: true, sorulsun: true }, BOYA: { izinli: true, sorulsun: true } },
};

/** Bir fuarın aktif kullanım kuralları, sıraya göre — GET /kural → kullanimKurallari şekli. */
function _mockKullanimKurallariOf(fuarId) {
  const rows = _mockKullanimKurali[fuarId] ?? {};
  return _mockKullanimKuraliKatalog
    .filter((k) => k.aktif)
    .slice()
    .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0) || a.kullanimKuraliId - b.kullanimKuraliId)
    .map((k) => {
      const own = rows[k.kod];
      return {
        ...k,
        izinli: own ? own.izinli : KULLANIM_KURALI_DEFAULTS.izinli,
        sorulsun: own ? own.sorulsun : KULLANIM_KURALI_DEFAULTS.sorulsun,
        configured: Boolean(own),
      };
    });
}

/** Geriye uyumlu cam/boya alanları — backend de CAM/BOYA'dan aynı şekilde türetiyor. */
function _legacyGlassPaint(kullanimKurallari) {
  const byKod = (kod) => (kullanimKurallari || []).find((r) => r.kod === kod);
  const cam = byKod(KULLANIM_KURALI_KOD.CAM);
  const boya = byKod(KULLANIM_KURALI_KOD.BOYA);
  return {
    camSorulsun: cam ? Boolean(cam.sorulsun) : KULLANIM_KURALI_DEFAULTS.sorulsun,
    camIzinli: cam ? Boolean(cam.izinli) : KULLANIM_KURALI_DEFAULTS.izinli,
    boyaSorulsun: boya ? Boolean(boya.sorulsun) : KULLANIM_KURALI_DEFAULTS.sorulsun,
    boyaIzinli: boya ? Boolean(boya.izinli) : KULLANIM_KURALI_DEFAULTS.izinli,
  };
}

// fuarId -> [{ id, minM2, maxM2, serbestKw }]
const _mockSerbestKw = {
  [GLOBAL_KURAL_FUAR_ID]: [
    { id: 1, minM2: 0, maxM2: 25, serbestKw: 10 },
    { id: 2, minM2: 25, maxM2: 40, serbestKw: 15 },
    { id: 3, minM2: 40, maxM2: null, serbestKw: 25 },
  ],
};

// fuarId -> standTypeId -> { yasak, minM2, maxM2, hMax }
const _mockStandTipiKural = {
  3: {
    1: { yasak: null, minM2: null, maxM2: null, hMax: 6 },      // Özel Stand (kök)
    5: { yasak: null, minM2: 24, maxM2: null, hMax: 4 },        // Ahşap
    7: { yasak: true, minM2: null, maxM2: null, hMax: null },   // Karma — yasak
  },
  [GLOBAL_KURAL_FUAR_ID]: {
    5: { yasak: null, minM2: 24, maxM2: null, hMax: 4 },
  },
};

/**
 * Mock salon listesi — canlı `GET /{fuarId}/h-max` → `halls[]` ile AYNI ham alanlar:
 * { salonToFuarId, salonId, salonName, fuarAlaniSalonId, salonHMaxTekKat, salonHMaxCiftKat,
 *   allowedHMax, ceilingHeight }  — çözümler _decorateHall'da eklenir.
 */
const _mockHalls = {
  3: [
    { salonToFuarId: 101, salonId: 11, salonName: 'Salon 1', fuarAlaniSalonId: 1, salonHMaxTekKat: 4, salonHMaxCiftKat: 6.5, allowedHMax: 7, ceilingHeight: 9 },
    { salonToFuarId: 102, salonId: 12, salonName: 'Salon 2', fuarAlaniSalonId: 2, salonHMaxTekKat: null, salonHMaxCiftKat: null, allowedHMax: 5, ceilingHeight: 8 },
    { salonToFuarId: 103, salonId: 13, salonName: 'Salon 3', fuarAlaniSalonId: null, salonHMaxTekKat: null, salonHMaxCiftKat: null, allowedHMax: null, ceilingHeight: null },
  ],
};

const _mockVenue = { 3: { fuarAlaniId: 1, fuarAlaniName: 'İstanbul Fuar Merkezi' } };

/** fuarId -> fuar_h_max. Canlıda 73 fuarın 0'ında dolu; mock'ta da boş başlıyor. */
const _mockFuarHMax = { 3: null };
const _mockFuarNames = { 3: 'Mock Fuar' };

/**
 * fuarAlaniId -> (kaldırılmış) fuar_alani_h_max. Yalnızca 1b bölümünün mock fuar alanı
 * listesi için duruyor; hiçbir çözüm artık bunu okumuyor.
 */
const _mockFuarAlaniHMax = { 1: null };

/** Bir fuar alanına bağlı mock fuarlar. */
const _mockFairsOfVenue = (fuarAlaniId) =>
  Object.keys(_mockVenue)
    .filter((id) => Number(_mockVenue[id].fuarAlaniId) === Number(fuarAlaniId))
    .map(Number);

/** Bir fuarın politika bağlamı — mock tarafındaki her hesap bununla başlar. */
function _mockPolicyContext(fuarId) {
  const venue = _mockVenue[fuarId] ?? { fuarAlaniId: null, fuarAlaniName: null };
  return { venue, fuarHMax: _mockFuarHMax[fuarId] ?? null };
}

const _mockStandTypeNames = {
  1: 'Özel Stand', 2: 'Sistem Stand', 5: 'Ahşap', 6: 'Metal', 7: 'Karma',
};
/** projectService._mockStandTypes'daki parent_id'lerin aynısı — mock miras zinciri için. */
const _mockStandTypeParents = { 1: null, 2: null, 5: 1, 6: 1, 7: 1 };

function parseError(error) {
  const data = error.response?.data;
  const message =
    typeof data === 'string' ? data : data?.message ?? error.message ?? i18n.t("services:fuarKuralService.bilinmeyenHata");
  const err = new Error(message);
  err.status = error.response?.status ?? null;
  // Çakışma (409) gövdesi çakışılan kaydı adlandırır — çağıran onu gösterebilsin diye taşınıyor.
  if (data && typeof data === 'object') err.data = data;
  if (Array.isArray(data?.violations)) err.violations = data.violations;
  // ceiling-height 409'unda `data` çakışan fuarların DİZİSİ; onu ayrıca taşıyoruz.
  if (Array.isArray(data?.data)) err.conflicts = data.data;
  throw err;
}

/**
 * Yazma yanıtlarındaki `{message, result, data}` zarfını açar.
 * ⚠️ GenericResultJson başarısızlığı HTTP 200 + result:false ile gelir — status
 *    koduna bakmak yetmez, `result`'ı okumak ZORUNLU.
 */
function unwrap(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && 'result' in payload) {
    if (payload.result === false) {
      const err = new Error(payload.message || i18n.t("services:fuarKuralService.islemTamamlanamadi"));
      err.status = 200;
      err.data = payload;
      throw err;
    }
    return payload.data ?? payload;
  }
  return payload;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. YÜKSEKLİK LİMİTLERİ (2026-09-15)
//    fuar.fuar_h_max + salon_to_fuar.salon_h_max_tek_kat / _cift_kat;
//    salonun varsayılanı ve fiziksel tavanı fuar_alani_salon'da (burada SALT OKUNUR)
// ─────────────────────────────────────────────────────────────────────────────

/** Mock salon satırına canlı yanıttaki çözümleri (tekKat / ciftKat / katsiz) ekler. */
function _decorateHall(h, { fuarHMax = null } = {}) {
  const base = {
    salonHMaxTekKat: h.salonHMaxTekKat, salonHMaxCiftKat: h.salonHMaxCiftKat,
    allowedHMax: h.allowedHMax, ceilingHeight: h.ceilingHeight, fuarHMax,
  };
  const katsiz = resolveHallHMax({ ...base, katTipi: null });
  return {
    ...h,
    registeredAtFairground: h.fuarAlaniSalonId != null,
    tekKat: resolveHallHMax({ ...base, katTipi: KAT_TIPI.TEK_KAT }),
    ciftKat: resolveHallHMax({ ...base, katTipi: KAT_TIPI.CIFT_KAT }),
    katsiz,
    hMax: katsiz.hMax,
    source: katsiz.source,
    clampedByCeiling: katsiz.clampedByCeiling,
  };
}

/**
 * Fuarın yükseklik resmi: fuarın kendi limiti + her salonun tek/çift katlı limitleri,
 * salonun fuar alanındaki varsayılanı ve fiziksel tavanı, her kat için çözüm.
 *
 * Yanıt: { fuarId, fuarHMax, fuarHMaxSet, fuarAlaniId, fuarAlaniName,
 *          halls: [{ salonToFuarId, salonId, salonName, fuarAlaniSalonId, registeredAtFairground,
 *                    salonHMaxTekKat, salonHMaxCiftKat, allowedHMax, ceilingHeight,
 *                    tekKat, ciftKat, katsiz: { hMax, source, clampedByCeiling, clampedByDefault },
 *                    hMax, source, clampedByCeiling (katsiz ile aynı) }] }
 *
 * ⚠️ 2026-09-15: `fuarAlaniHMax`, `fairHeadroom`, `salonHMax`, `headroom` YANITTA YOK.
 * ⚠️ 404 döner (fuar yoksa).
 * GET /fuar/{fuarId}/h-max
 */
export async function getFuarHMax(fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { venue, fuarHMax } = _mockPolicyContext(fuarId);
    return {
      fuarId: Number(fuarId),
      fuarHMax, fuarHMaxSet: fuarHMax != null,
      ...venue,
      halls: (_mockHalls[fuarId] ?? []).map((h) => _decorateHall(h, { fuarHMax })),
    };
  }
  try {
    const res = await axios.get(`${BASE}/${fuarId}/h-max`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * BU FUARIN kendi yükseklik limiti (Politika Limiti). `null` temizler.
 *
 * ⚠️ Gövdeye `hMax` anahtarını MUTLAKA koyun; atlamak (null göndermek değil) 400.
 * ⚠️ 409 → bu fuarın salonlarından bazılarının tek/çift katlı limiti ZATEN daha yüksek;
 *    gövde onları listeler (err.conflicts). 2026-09-15'ten beri üstte engelleyen seviye yok.
 * PUT /fuar/{fuarId}/h-max
 */
export async function setFuarHMax(fuarId, hMax, { forceLive = false } = {}) {
  const value = hMax == null || hMax === '' ? null : Number(hMax);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { venue } = _mockPolicyContext(fuarId);
    if (value != null) {
      const offenders = (_mockHalls[fuarId] ?? [])
        .filter((h) => (h.salonHMaxTekKat != null && h.salonHMaxTekKat > value)
          || (h.salonHMaxCiftKat != null && h.salonHMaxCiftKat > value))
        .map((h) => ({
          salonToFuarId: h.salonToFuarId, salonId: h.salonId, salonName: h.salonName,
          salonHMaxTekKat: h.salonHMaxTekKat, salonHMaxCiftKat: h.salonHMaxCiftKat,
        }));
      if (offenders.length) {
        const err = new Error('Bu fuarda daha yükseğine izin verilmiş salonlar var — önce onların limitini indirin.');
        err.status = 409;
        err.conflicts = offenders;
        throw err;
      }
    }
    _mockFuarHMax[fuarId] = value;
    return { fuarId: Number(fuarId), fuarHMax: value, fuarHMaxSet: value != null, ...venue };
  }
  try {
    const res = await axios.put(`${BASE}/${fuarId}/h-max`, { hMax: value }, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Backlog #48 (2026-09-17/18): fuarın POLİTİKA limitini fuara bağlı HER SALONA, istenen kat(lar)
 * için tek seferde yazar — tarayıcının salon başına ayrı PUT atıp yarıda kalmasını önler.
 *
 * `kat`: KAT_TIPI.TEK_KAT | KAT_TIPI.CIFT_KAT | 'BOTH'.
 *
 * Zaten kendi değeri olan bir salon da EZİLİR — bu toplu bir SET, yalnızca boşları doldurma değil.
 * Bir salonun fuar alanı sınırı (izin verilen yükseklik / fiziksel tavan, #23) fuar limitinden
 * DÜŞÜKSE o salon ATLANIR ve `skippedHalls`'da adlandırılır; hiçbir şey bloklanmaz, sadece o salon
 * dokunulmadan kalır. `fuarHMax` boşsa (henüz kaydedilmemişse) 400.
 *
 * ⚠️ Her Proje Beyanı okuması kabul edilmiş beyanları GÜNCEL limite göre yeniden yargılıyor —
 * salon değerini düşüren bir toplu uygulama üzerindeki beyanları KİLİTLEYEBİLİR
 * (HEIGHT_ABOVE_LIMIT). Çağıran taraf (bkz. handleApplyFairLimitToHalls) önce onaylatmalı.
 *
 * Yanıt: getFuarHMax ile aynı şekil + { appliedCount, skippedHalls: [{ salonToFuarId, salonId,
 * salonName, cap, reason }] }.
 * POST /fuar/{fuarId}/h-max/apply-to-halls
 */
export async function applyFuarHMaxToHalls(fuarId, kat, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { venue, fuarHMax } = _mockPolicyContext(fuarId);
    if (fuarHMax == null) {
      throw new Error('Bu fuarın henüz bir yükseklik limiti yok — salonlara uygulamadan önce kaydedin.');
    }
    const applyTek = kat === KAT_TIPI.TEK_KAT || kat === 'BOTH';
    const applyCift = kat === KAT_TIPI.CIFT_KAT || kat === 'BOTH';
    const skippedHalls = [];
    let appliedCount = 0;
    (_mockHalls[fuarId] ?? []).forEach((h) => {
      const cap = [h.ceilingHeight, h.allowedHMax].filter((v) => v != null).reduce(
        (min, v) => (min == null || v < min ? v : min), null
      );
      if (cap != null && fuarHMax > cap) {
        skippedHalls.push({
          salonToFuarId: h.salonToFuarId, salonId: h.salonId, salonName: h.salonName, cap,
          reason: `Fuar limiti ${fuarHMax} m, salonun sınırı olan ${cap} m değerini aşıyor.`,
        });
        return;
      }
      if (applyTek) h.salonHMaxTekKat = fuarHMax;
      if (applyCift) h.salonHMaxCiftKat = fuarHMax;
      appliedCount += 1;
    });
    return {
      fuarId: Number(fuarId), fuarHMax, fuarHMaxSet: true, ...venue,
      halls: (_mockHalls[fuarId] ?? []).map((h) => _decorateHall(h, { fuarHMax })),
      appliedCount, skippedHalls,
    };
  }
  try {
    const res = await axios.post(
      `${BASE}/${fuarId}/h-max/apply-to-halls`, { kat }, { headers: getAuthHeader() }
    );
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * ⚠️ KALDIRILDI 2026-09-15 — backend 410 döner. Fuar alanı geneli limit yerine her salonun
 * varsayılan izin verilen yüksekliği var: fuarAlaniSalonService.updateSalon(..., { allowedHMax }).
 * PUT /fuar/alani/{fuarAlaniId}/h-max
 */
export async function setFuarAlaniHMax() {
  throw new Error(
    i18n.t("services:fuarKuralService.fuarAlaniGeneliYukseklikLimiti")
  );
}

/**
 * Bir salonun BU FUARDAKİ yükseklik limitleri, KAT sayısına göre.
 * `limits` = { tekKatHMax?, ciftKatHMax? } — yalnızca gönderilen anahtar değişir, null temizler.
 * Geriye dönük: bir sayı verilirse iki kata da yazılır.
 *
 * ⚠️ 400 → değer salonun fiziksel tavanını, salonun varsayılan izin verilen yüksekliğini veya
 *    fuar limitini aşıyor; mesaj hangisi olduğunu söyler.
 * PUT /fuar/salon-to-fuar/{salonToFuarId}/h-max
 */
export async function setSalonHMax(salonToFuarId, limits, { forceLive = false } = {}) {
  const toVal = (v) => (v == null || v === '' ? null : Number(v));
  const body = {};
  if (limits != null && typeof limits === 'object') {
    if ('tekKatHMax' in limits) body.tekKatHMax = toVal(limits.tekKatHMax);
    if ('ciftKatHMax' in limits) body.ciftKatHMax = toVal(limits.ciftKatHMax);
  } else {
    body.tekKatHMax = toVal(limits);
    body.ciftKatHMax = toVal(limits);
  }
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    let updated = null;
    Object.keys(_mockHalls).forEach((fuarId) => {
      const { fuarHMax } = _mockPolicyContext(fuarId);
      _mockHalls[fuarId].forEach((h) => {
        if (h.salonToFuarId !== Number(salonToFuarId)) return;
        [['tekKatHMax', 'salonHMaxTekKat', 'Tek katlı'], ['ciftKatHMax', 'salonHMaxCiftKat', 'Çift katlı']]
          .forEach(([key, field, label]) => {
            if (!(key in body) || body[key] == null) return;
            // WAS: hallLimitCeiling({ fuarHMax, ... }) — #48 dropped the fuar-limit cap here too.
            const bound = hallLimitCeiling({ allowedHMax: h.allowedHMax, ceilingHeight: h.ceilingHeight });
            if (bound.value != null && body[key] > bound.value) {
              throw new Error(`${label} limit ${body[key]} m, ${HMAX_SOURCE_LABELS[bound.source].toLowerCase()} (${bound.value} m) değerini aşıyor.`);
            }
            h[field] = body[key];
          });
        ['tekKatHMax', 'ciftKatHMax'].forEach((key) => {
          if (key in body && body[key] == null) h[key === 'tekKatHMax' ? 'salonHMaxTekKat' : 'salonHMaxCiftKat'] = null;
        });
        updated = _decorateHall(h, { fuarHMax });
      });
    });
    return updated ?? { salonToFuarId: Number(salonToFuarId) };
  }
  try {
    const res = await axios.put(`${BASE}/salon-to-fuar/${salonToFuarId}/h-max`, body, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Salonun bir FUAR ALANINDAKİ fiziksel tavanı. 2026-09-15'ten beri fuar_alani_salon'a yazar;
 * yeni ekranlar fuarAlaniSalonService.updateSalon'u kullanır, bu uç eski çağıranlar için duruyor.
 *
 * ⚠️ 409 → bu mekândaki fuarların bu salondaki limitleri daha yüksek (err.conflicts).
 * ⚠️ 400 → salon bu fuar alanında kayıtlı değil, ya da tavan varsayılanın altında kalıyor.
 * PUT /fuar/alani/{fuarAlaniId}/salon/{salonId}/ceiling-height
 */
export async function setSalonCeilingHeight(fuarAlaniId, salonId, ceilingHeight, { forceLive = false } = {}) {
  const value = ceilingHeight == null || ceilingHeight === '' ? null : Number(ceilingHeight);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    let updated = null;
    Object.keys(_mockHalls).forEach((fuarId) => {
      _mockHalls[fuarId].forEach((h) => {
        if (h.salonId !== Number(salonId)) return;
        h.ceilingHeight = value;
        updated = _decorateHall(h, _mockPolicyContext(fuarId));
      });
    });
    return updated ?? { salonId: Number(salonId), ceilingHeight: value };
  }
  try {
    const res = await axios.put(`${BASE}/alani/${fuarAlaniId}/salon/${salonId}/ceiling-height`,
      { ceilingHeight: value }, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 1b. FUAR ALANI GÖRÜNÜMÜ — fiziksel tavanlar, fuardan BAĞIMSIZ
//
// ⚠️ NEDEN BURADA BİR BİRLEŞTİRME VAR. Fiziksel tavan (salon_base_data.ceiling_height)
//    ve fuar alanı sınırı (fuar_alani.fuar_alani_h_max) FUARDAN BAĞIMSIZ gerçeklerdir —
//    aynı salonun tavanı, o mekânda açılan her fuar için aynıdır. Ama backend'de
//    fuar alanına göre OKUYAN bir uç YOK: iki değeri de sadece `GET /fuar/{id}/h-max`
//    döndürüyor ve o uç bir FUAR ister. `/fuaralani/**` ad alanının tamamı 404
//    (bkz. util/knowledgebase/salon-kroki-endpoints.md).
//
//    Bu yüzden fuar alanı görünümünü FE birleştiriyor: mekânın fuarlarını buluyor,
//    her biri için /h-max okuyor ve salonları salonId'ye göre teklileştiriyor. Aynı
//    salon birden çok fuarda görünür; tavanı hepsinde AYNI sayıdır (tek satır), o
//    yüzden ilk dolu değer alınır. Fuara özel olan `salonHMax` ise salona "söz
//    verilmiş" en yüksek değer olarak taşınır — tavanı indirmenin 409'unu ekran
//    kaydetmeden önce gösterebilsin diye.
//
//    TODO(backend): `GET /fuar/alani/{id}/salonlar` (veya /h-max) gelirse bu
//    birleştirme tek çağrıya iner — çağıranlar aynı şekli görmeye devam eder.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fuar alanları — Fuar Alanı Tavanları ekranının seçicisi.
 * ⚠️ Gerçek login ister (401). `id` alanı `fuarAlaniId` olarak normalize edilir.
 * GET /fuar/listFuarAlani
 */
export async function listFuarAlanlari({ forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return Object.entries(_mockFuarAlaniHMax).map(([id]) => {
      const venue = Object.values(_mockVenue).find((v) => Number(v.fuarAlaniId) === Number(id));
      return { fuarAlaniId: Number(id), fuarAlaniName: venue?.fuarAlaniName ?? `Fuar Alanı ${id}` };
    });
  }
  try {
    const res = await axios.get(`${BASE}/listFuarAlani`, { headers: getAuthHeader() });
    const rows = Array.isArray(res.data) ? res.data : [];
    return rows.map((a) => ({
      fuarAlaniId: a.fuarAlaniId ?? a.id,
      fuarAlaniName: a.fuarAlaniName || i18n.t("services:fuarKuralService.fuarAlani", { v: a.fuarAlaniId ?? a.id }),
    }));
  } catch (e) { parseError(e); }
}

/**
 * Bir fuar alanına bağlı fuarlar. `/fuar/list` düz dizi döner ve her satır
 * `fuarAlaniId` taşır (bkz. Fairs/fairsform.js) — ayrı bir uç yok, filtre FE'de.
 * GET /fuar/list?languageId=1
 */
/** Bir mekân için okunacak en fazla fuar — 10 mekâna 73 fuar düşüyor, tavan bol. */
const MAX_VENUE_FAIRS = 40;

async function _fairsOfVenue(fuarAlaniId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockFairsOfVenue(fuarAlaniId).map((id) => ({
      fuarId: id, fuarName: _mockFuarNames[id] ?? `Fuar ${id}`,
    }));
  }
  const res = await axios.get(`${BASE}/list`, {
    // WAS (until #332): params: { languageId: 1 },
    params: { languageId: currentLanguageId() }, headers: getAuthHeader(),
  });
  const rows = Array.isArray(res.data) ? res.data : (res.data?.content || []);
  return rows
    .filter((f) => Number(f.fuarAlaniId) === Number(fuarAlaniId))
    .map((f) => ({ fuarId: f.fuarId ?? f.id, fuarName: f.fuarName ?? `Fuar ${f.fuarId ?? f.id}` }));
}

/**
 * Bir fuar alanının TAVAN GÖRÜNÜMÜ: mekânın kendi politika sınırı + salonlarının
 * fiziksel tavanları + her salona hangi fuarın ne söz verdiği.
 *
 * Dönen şekil:
 * {
 *   fuarAlaniId, fuarAlaniName, fuarAlaniHMax, fuarAlaniHMaxSet,
 *   fairs:   [{ fuarId, fuarName, fuarHMax }],
 *   halls:   [{ salonId, salonName, ceilingHeight,
 *               maxPromised,                       // en yüksek salonHMax (null = söz yok)
 *               promises: [{ fuarId, fuarName, salonToFuarId, salonHMax }] }],
 *   partial: true    // bazı fuarların /h-max okuması patladı — liste eksik olabilir
 * }
 *
 * ⚠️ `fuarAlaniHMax` her fuarın yanıtında AYNI gelir; ilk başarılı okumadan alınır.
 *    Mekânın hiç fuarı yoksa okunamaz (uç fuar ister) ve `null` döner — bu "sınır
 *    yok" değil "okunamadı" demektir, ekran ikisini ayırmak zorunda.
 */
export async function getFuarAlaniTavanlari(fuarAlaniId, { forceLive = false } = {}) {
  const fairs = await _fairsOfVenue(fuarAlaniId, { forceLive });

  const hallsById = new Map();
  let fuarAlaniName = null;
  let fuarAlaniHMax = null;
  let fuarAlaniHMaxSet = false;
  let readAny = false;
  let partial = false;

  // Bir mekânda onlarca fuar olabiliyor; sırayla okumak ekranı dakikalarca bekletir.
  // Hepsi paralel, allSettled ile — tek bir fuarın patlaması mekânı gizlememeli.
  const capped = fairs.slice(0, MAX_VENUE_FAIRS);
  const truncated = fairs.length > capped.length;
  const settled = await Promise.allSettled(
    capped.map((f) => getFuarHMax(f.fuarId, { forceLive }))
  );

  const fairRows = [];
  capped.forEach((f, i) => {
    const outcome = settled[i];
    if (outcome.status !== 'fulfilled') {
      partial = true;
      fairRows.push({ ...f, fuarHMax: null, unreadable: true });
      return;
    }
    const h = outcome.value;
    readAny = true;
    fairRows.push({ ...f, fuarHMax: h?.fuarHMax ?? null });
    if (fuarAlaniName == null) fuarAlaniName = h?.fuarAlaniName ?? null;
    if (!fuarAlaniHMaxSet && h?.fuarAlaniHMax != null) {
      fuarAlaniHMax = h.fuarAlaniHMax;
      fuarAlaniHMaxSet = true;
    }
    (h?.halls || []).forEach((row) => {
      if (row.salonId == null) return;
      const prev = hallsById.get(row.salonId) ?? {
        salonId: row.salonId, salonName: row.salonName, ceilingHeight: null, promises: [],
      };
      // Tavan (salon, fuar alanı) çiftine ait TEK satır — ilk dolu değer yeterli.
      if (prev.ceilingHeight == null && row.ceilingHeight != null) prev.ceilingHeight = row.ceilingHeight;
      if (!prev.salonName && row.salonName) prev.salonName = row.salonName;
      if (row.salonHMax != null) {
        prev.promises.push({
          fuarId: f.fuarId, fuarName: f.fuarName,
          salonToFuarId: row.salonToFuarId, salonHMax: row.salonHMax,
        });
      }
      hallsById.set(row.salonId, prev);
    });
  });

  const halls = [...hallsById.values()]
    .map((h) => ({
      ...h,
      // 409'u kaydetmeden önce gösterebilmek için: bu salonda söz verilmiş EN YÜKSEK değer.
      maxPromised: h.promises.length
        ? Math.max(...h.promises.map((p) => Number(p.salonHMax)))
        : null,
    }))
    .sort((a, b) => (a.salonName || '').localeCompare(b.salonName || '', 'tr'));

  return {
    fuarAlaniId: Number(fuarAlaniId),
    fuarAlaniName,
    fuarAlaniHMax,
    fuarAlaniHMaxSet,
    // Hiç fuar okunamadıysa fuarAlaniHMax "yok" değil "bilinmiyor".
    fuarAlaniHMaxKnown: readAny,
    fairs: fairRows,
    halls,
    // partial = bir fuar okunamadı · truncated = mekânda tavandan çok fuar var.
    // İkisi de "liste eksik olabilir" demek; ekran bunu söylemek zorunda.
    partial: partial || truncated,
    truncated,
  };
}

/**
 * Çözülmüş limit + hangi katmanın kazandığı.
 * Kat sayısı hangi salon limitinin geçerli olduğunu belirler: `standTypeId` (ağaçta yürünür)
 * ya da doğrudan `katTipi` (TEK_KAT | CIFT_KAT). İkisi de yoksa salon limitleri atlanır.
 * Yanıt: hMax, source, katTipi, hallHMax (o katın limiti), hallHMaxTekKat, hallHMaxCiftKat,
 *        allowedHMax, ceilingHeight, fuarHMax, clampedByCeiling, clampedByDefault …
 * GET /fuar/{fuarId}/h-max/effective?standId=&standTypeId=&katTipi=
 */
export async function getEffectiveHMax(fuarId, {
  standId = null, standTypeId = null, katTipi = null, forceLive = false,
} = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const { venue, fuarHMax } = _mockPolicyContext(fuarId);
    const hall = standId ? (_mockHalls[fuarId] ?? [])[0] ?? null : null;
    const r = resolveHallHMax({
      salonHMaxTekKat: hall?.salonHMaxTekKat ?? null, salonHMaxCiftKat: hall?.salonHMaxCiftKat ?? null,
      katTipi, fuarHMax, allowedHMax: hall?.allowedHMax ?? null, ceilingHeight: hall?.ceilingHeight ?? null,
    });
    return {
      fuarId: Number(fuarId), standId: standId ?? null, katTipi,
      hMax: r.hMax, source: r.source,
      clampedByCeiling: r.clampedByCeiling, clampedByDefault: r.clampedByDefault,
      hallHMax: katTipi === KAT_TIPI.TEK_KAT ? hall?.salonHMaxTekKat ?? null
        : katTipi === KAT_TIPI.CIFT_KAT ? hall?.salonHMaxCiftKat ?? null : null,
      hallHMaxTekKat: hall?.salonHMaxTekKat ?? null, hallHMaxCiftKat: hall?.salonHMaxCiftKat ?? null,
      hallName: hall?.salonName ?? null, salonId: hall?.salonId ?? null,
      allowedHMax: hall?.allowedHMax ?? null, ceilingHeight: hall?.ceilingHeight ?? null,
      fuarHMax, ambiguousHall: false, ...venue,
    };
  }
  try {
    const params = {};
    if (standId != null) params.standId = standId;
    if (standTypeId != null) params.standTypeId = standTypeId;
    if (katTipi != null) params.katTipi = katTipi;
    const res = await axios.get(`${BASE}/${fuarId}/h-max/effective`, { params, headers: getAuthHeader() });
    return res.data;
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. FUAR KURALLARI (fuar_kural — skaler ayarlar)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fuarın skaler kural seti + serbest kW kademeleri.
 * ASLA 404 dönmez — satır yoksa varsayılanlar + `configured: false` gelir. Değerlere
 * bakarak tahmin etmeyin, bayrağı okuyun: "izinli ve sorulmuyor" ile "hiç kaydedilmemiş"
 * aksi halde ayırt edilemez.
 *
 * ⚠️ `serbestKwKademeleri` `configured:false` iken bile dolu gelebilir — kademeler
 *    kendi tablosunda yaşar, skaler ayarlar hiç kaydedilmemiş olsa da bant olabilir.
 *
 * 2026-09-17 ek alanlar: serbestKwMode (NONE|FLAT|BLOCK|BANDS), serbestKwBlokKw,
 * serbestKwBlokM2, kullanimKurallari[{ kullanimKuraliId, kod, ad, beyanSorusu, yasakMesaji,
 * sira, aktif, izinli, sorulsun, configured }] (yalnız aktif katalog, sıraya göre).
 * boya/cam alanları ve kimlikDogrulamaZorunlu (hep true) uyumluluk için hâlâ geliyor.
 * GET /fuar/{fuarId}/kural
 */
export async function getFuarKural(fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const base = _mockKural[fuarId] ?? { ...KURAL_DEFAULTS, configured: false };
    const bands = _mockSerbestKw[fuarId] ?? [];
    const kullanimKurallari = _mockKullanimKurallariOf(fuarId);
    return {
      fuarId: Number(fuarId),
      ...base,
      serbestKwBlokKw: base.serbestKwBlokKw ?? null,
      serbestKwBlokM2: base.serbestKwBlokM2 ?? null,
      komsuDuvarFarkM: base.komsuDuvarFarkM ?? null,
      minAciklikYuzde: base.minAciklikYuzde ?? null,
      kullanimKurallari,
      ..._legacyGlassPaint(kullanimKurallari),
      kimlikDogrulamaZorunlu: true,
      serbestKwKademeleri: bands,
      serbestKwMode: deriveSerbestKwMode({ ...base, serbestKwKademeleri: bands }),
    };
  }
  try {
    const res = await axios.get(`${BASE}/${fuarId}/kural`, { headers: getAuthHeader() });
    const data = res.data ?? {};
    // serbestKwMode backend'de türetiliyor ama gelmezse burada türetiyoruz —
    // editör null'lara bakarak mod tahmin etmemeli.
    return {
      ...data,
      serbestKwBlokKw: data.serbestKwBlokKw ?? null,
      serbestKwBlokM2: data.serbestKwBlokM2 ?? null,
      komsuDuvarFarkM: data.komsuDuvarFarkM ?? null,
      minAciklikYuzde: data.minAciklikYuzde ?? null,
      kullanimKurallari: Array.isArray(data.kullanimKurallari) ? data.kullanimKurallari : [],
      serbestKwMode: data.serbestKwMode ?? deriveSerbestKwMode(data),
    };
  } catch (e) { parseError(e); }
}

/**
 * Kullanım kuralı satırlarını PUT şekline indirger: [{ kod, izinli, sorulsun }].
 * Eksik bayrak VARSAYILANA düşer (izinli=true, sorulsun=false) — `Boolean(undefined)`
 * bir kuralı sessizce YASAKLAMASIN diye.
 * Dizi yoksa ama eski camIzinli/boyaIzinli… alanları varsa (eski bir çağıran) CAM/BOYA'ya
 * çevrilir: dizi OTORİTER olduğu için boş göndermek iki kuralı da varsayılana sıfırlardı.
 */
function _toKullanimKuraliPayload(body) {
  const flag = (v, dflt) => (v == null ? dflt : Boolean(v));
  if (Array.isArray(body?.kullanimKurallari)) {
    return body.kullanimKurallari
      .filter((r) => r && r.kod)
      .map((r) => ({
        kod: r.kod,
        izinli: flag(r.izinli, KULLANIM_KURALI_DEFAULTS.izinli),
        sorulsun: flag(r.sorulsun, KULLANIM_KURALI_DEFAULTS.sorulsun),
      }));
  }
  const legacy = [];
  if (body?.camIzinli != null || body?.camSorulsun != null) {
    legacy.push({
      kod: KULLANIM_KURALI_KOD.CAM,
      izinli: flag(body.camIzinli, KULLANIM_KURALI_DEFAULTS.izinli),
      sorulsun: flag(body.camSorulsun, KULLANIM_KURALI_DEFAULTS.sorulsun),
    });
  }
  if (body?.boyaIzinli != null || body?.boyaSorulsun != null) {
    legacy.push({
      kod: KULLANIM_KURALI_KOD.BOYA,
      izinli: flag(body.boyaIzinli, KULLANIM_KURALI_DEFAULTS.izinli),
      sorulsun: flag(body.boyaSorulsun, KULLANIM_KURALI_DEFAULTS.sorulsun),
    });
  }
  return legacy;
}

/**
 * ⚠️ TAM DEĞİŞTİRME. Formun gösterdiği HER alanı her seferinde gönderin —
 * atlanan bir değer eski değerine değil VARSAYILANINA döner (sessiz veri kaybı).
 * `kullanimKurallari` OTORİTER: ekranda gösterilen her kuralı gönderin; dizide olmayan
 * kod varsayılana (izinli, sorulmuyor) döner. Bilinmeyen/pasif kod → 400.
 *
 * Gövde: { serbestKw, serbestKwBlokKw, serbestKwBlokM2, m2Zorunlu, kullanimKurallari }
 * ⚠️ En fazla BİR mod: serbestKw ile blok çifti birlikte → 400; kademe varken ikisinden
 *    biri → 400. Blok iki sayıyı birden ister, blokM2 > 0, blokKw >= 0. Mod değiştirmek
 *    için önce diğerini temizleyin (sıra sayfalarda, bkz. handleSaveKural).
 * ⚠️ 2026-09-17: kimlikDogrulamaZorunlu ve boya/cam alanları ARTIK GÖNDERİLMİYOR.
 * PUT /fuar/{fuarId}/kural
 */
export async function saveFuarKural(fuarId, body, { forceLive = false } = {}) {
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const payload = {
    serbestKw: num(body.serbestKw),
    serbestKwBlokKw: num(body.serbestKwBlokKw),
    serbestKwBlokM2: num(body.serbestKwBlokM2),
    // Ayar değil sabit — ekranda anahtarı yok, gövdedeki değere de bakılmıyor.
    m2Zorunlu: M2_ZORUNLU_SABIT,
    kullanimKurallari: _toKullanimKuraliPayload(body),
  };
  // FDS #674 / #675 (duvar kuralları): yalnızca çağıran taşıyorsa gönderilir. Gövdede OLMAYAN alanı backend olduğu gibi bırakır
  // (bu alanları bilmeyen eski bir çağıran kuralı kapatmasın), açık null onu temizler.
  if (body.komsuDuvarFarkM !== undefined) payload.komsuDuvarFarkM = num(body.komsuDuvarFarkM);
  if (body.minAciklikYuzde !== undefined) payload.minAciklikYuzde = num(body.minAciklikYuzde);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    // Backend'in 400'lerinin aynası — mock'ta da yanlış sıra/karışım yakalansın.
    const blokAny = payload.serbestKwBlokKw != null || payload.serbestKwBlokM2 != null;
    if (payload.serbestKw != null && blokAny) {
      throw _mockError(400, 'Sabit serbest kW ile matematiksel (blok) hak birlikte tanımlanamaz.');
    }
    if ((payload.serbestKw != null || blokAny) && (_mockSerbestKw[fuarId] ?? []).length > 0) {
      throw _mockError(400, 'Bu fuarda serbest kW kademeleri var — önce kademeleri temizleyin.');
    }
    if (blokAny) {
      const errs = validateSerbestKwBlok(payload.serbestKwBlokKw, payload.serbestKwBlokM2);
      if (errs.length) throw _mockError(400, errs.join(' '));
    }
    const activeKods = new Set(_mockKullanimKuraliKatalog.filter((k) => k.aktif).map((k) => k.kod));
    const bad = payload.kullanimKurallari.find((r) => !activeKods.has(r.kod));
    if (bad) throw _mockError(400, `Bilinmeyen ya da pasif kullanım kuralı: ${bad.kod}`);

    _mockKural[fuarId] = {
      serbestKw: payload.serbestKw,
      serbestKwBlokKw: payload.serbestKwBlokKw,
      serbestKwBlokM2: payload.serbestKwBlokM2,
      m2Zorunlu: payload.m2Zorunlu,
      komsuDuvarFarkM: 'komsuDuvarFarkM' in payload ? payload.komsuDuvarFarkM : (_mockKural[fuarId]?.komsuDuvarFarkM ?? null),
      minAciklikYuzde: 'minAciklikYuzde' in payload ? payload.minAciklikYuzde : (_mockKural[fuarId]?.minAciklikYuzde ?? null),
      configured: true,
    };
    // Otoriter dizi: haritayı baştan kur — gönderilmeyen kod varsayılana döner.
    _mockKullanimKurali[fuarId] = Object.fromEntries(
      payload.kullanimKurallari.map((r) => [r.kod, { izinli: r.izinli, sorulsun: r.sorulsun }])
    );
    return getFuarKural(fuarId);
  }
  try {
    const res = await axios.put(`${BASE}/${fuarId}/kural`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 2b. SERBEST kW KADEMELERİ (fuar_serbest_kw)
// ─────────────────────────────────────────────────────────────────────────────

/** GET /fuar/{fuarId}/kural/serbest-kw — düşükten yükseğe. Boş liste = kademe yok. */
export async function getSerbestKwBands(fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockSerbestKw[fuarId] ?? [];
  }
  try {
    const res = await axios.get(`${BASE}/${fuarId}/kural/serbest-kw`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * ⚠️ TAM DEĞİŞTİRME — satır bazlı upsert DEĞİL. Kısıt kümenin tamamı üzerinde
 * (hiçbir iki bant kesişemez), dolayısıyla kaydedilebilir bir "yarı yol" hâli yok.
 * `[]` tabloyu temizler. Gönderilen satırlardaki `id` yok sayılır.
 * ⚠️ Fuarda sabit serbestKw YA DA blok çifti doluyken kademe yazmak 400 — önce
 *    PUT /kural ile üçünü de null'layın, sonra kademeleri yazın.
 * PUT /fuar/{fuarId}/kural/serbest-kw
 */
export async function saveSerbestKwBands(fuarId, bands, { forceLive = false } = {}) {
  const payload = (bands || []).map((b) => ({
    minM2: b.minM2 === '' || b.minM2 == null ? null : Number(b.minM2),
    maxM2: b.maxM2 === '' || b.maxM2 == null ? null : Number(b.maxM2),
    serbestKw: b.serbestKw === '' || b.serbestKw == null ? null : Number(b.serbestKw),
  }));
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const k = _mockKural[fuarId];
    if (payload.length > 0 && k
      && (k.serbestKw != null || k.serbestKwBlokKw != null || k.serbestKwBlokM2 != null)) {
      throw _mockError(400, 'Bu fuarda sabit ya da matematiksel serbest kW tanımlı — önce onu temizleyin.');
    }
    _mockSerbestKw[fuarId] = payload.map((b, i) => ({ id: i + 1, ...b }));
    return _mockSerbestKw[fuarId];
  }
  try {
    const res = await axios.put(`${BASE}/${fuarId}/kural/serbest-kw`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 2c. KULLANIM KURALI KATALOĞU (#53) — fuardan bağımsız, admin yönetir
//     Fuarın değerleri (izinli/sorulsun) GET/PUT /kural → kullanimKurallari üzerinden.
// ─────────────────────────────────────────────────────────────────────────────

/** Mock kod üretimi — backend'in "ad'dan kod" kuralına yakın: Türkçe harf → ASCII, BÜYÜK_HARF. */
function _kodFromAd(ad) {
  const map = { ç: 'C', Ç: 'C', ğ: 'G', Ğ: 'G', ı: 'I', İ: 'I', ö: 'O', Ö: 'O', ş: 'S', Ş: 'S', ü: 'U', Ü: 'U' };
  return String(ad || '')
    .replace(/[çÇğĞıİöÖşŞüÜ]/g, (c) => map[c])
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Katalog listesi. `includeInactive` pasifleri de getirir (yönetim ekranı).
 * Yanıt: [{ kullanimKuraliId, kod, ad, beyanSorusu, yasakMesaji, sira, aktif, fuarSayisi }]
 * GET /fuar/kullanim-kurallari?includeInactive=
 */
export async function listKullanimKurallari({ includeInactive = false, forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockKullanimKuraliKatalog
      .filter((k) => includeInactive || k.aktif)
      .slice()
      .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0) || a.kullanimKuraliId - b.kullanimKuraliId)
      .map((k) => ({
        ...k,
        fuarSayisi: Object.entries(_mockKullanimKurali).filter(([fid, rows]) => Number(fid) !== 0 && rows && rows[k.kod]).length,
      }));
  }
  try {
    const res = await axios.get(`${BASE}/kullanim-kurallari`, {
      params: { includeInactive: Boolean(includeInactive) }, headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * Yeni kural. `kod` verilmezse backend ad'dan üretir; kod SONRADAN DEĞİŞMEZ.
 * 201 { message, result, data: item } · 409 kod zaten var · 400 ad/beyanSorusu eksik.
 * POST /fuar/kullanim-kurallari
 */
export async function createKullanimKurali(body, { forceLive = false } = {}) {
  const trimOrNull = (v) => (v == null || String(v).trim() === '' ? null : String(v).trim());
  const payload = {
    ad: trimOrNull(body?.ad),
    beyanSorusu: trimOrNull(body?.beyanSorusu),
    yasakMesaji: trimOrNull(body?.yasakMesaji),
    // #337: the English texts (kullanim_kurali_text). No adEn = no English row; the rule then reads in Turkish.
    adEn: trimOrNull(body?.adEn),
    beyanSorusuEn: trimOrNull(body?.beyanSorusuEn),
    yasakMesajiEn: trimOrNull(body?.yasakMesajiEn),
  };
  if (body?.sira !== '' && body?.sira != null) payload.sira = Number(body.sira);
  if (trimOrNull(body?.kod)) payload.kod = trimOrNull(body.kod);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    if (!payload.ad || !payload.beyanSorusu) throw _mockError(400, 'Ad ve beyan sorusu zorunlu.');
    const kod = payload.kod ? _kodFromAd(payload.kod) : _kodFromAd(payload.ad);
    if (!kod) throw _mockError(400, 'Addan geçerli bir kod üretilemedi.');
    if (_mockKullanimKuraliKatalog.some((k) => k.kod === kod)) {
      throw _mockError(409, `"${kod}" kodlu bir kullanım kuralı zaten var.`);
    }
    const item = {
      kullanimKuraliId: Math.max(0, ..._mockKullanimKuraliKatalog.map((k) => k.kullanimKuraliId)) + 1,
      kod,
      ad: payload.ad,
      beyanSorusu: payload.beyanSorusu,
      yasakMesaji: payload.yasakMesaji,
      adEn: payload.adEn, beyanSorusuEn: payload.beyanSorusuEn, yasakMesajiEn: payload.yasakMesajiEn, // #337
      sira: payload.sira ?? _mockKullanimKuraliKatalog.length + 1,
      aktif: true,
    };
    _mockKullanimKuraliKatalog.push(item);
    return { ...item, fuarSayisi: 0 };
  }
  try {
    const res = await axios.post(`${BASE}/kullanim-kurallari`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Kural güncelle — PATCH gibi: yalnızca verilen anahtarlar gönderilir. `kod` değişmez,
 * SİLME YOK: emekliye ayırmak için { aktif: false }.
 * PUT /fuar/kullanim-kurallari/{id}
 */
export async function updateKullanimKurali(id, patch, { forceLive = false } = {}) {
  const payload = {};
  // WAS: ['ad', 'beyanSorusu', 'yasakMesaji'].forEach((key) => {
  // #337: + the English texts; a blank adEn is sent as null and removes the English row.
  ['ad', 'beyanSorusu', 'yasakMesaji', 'adEn', 'beyanSorusuEn', 'yasakMesajiEn'].forEach((key) => {
    if (patch && key in patch) {
      const v = patch[key];
      payload[key] = v == null || String(v).trim() === '' ? null : String(v).trim();
    }
  });
  // Boş sıra "değiştirme" demek — null göndermek sırayı silmeye çalışırdı.
  if (patch && patch.sira !== '' && patch.sira != null) payload.sira = Number(patch.sira);
  if (patch && 'aktif' in patch) payload.aktif = Boolean(patch.aktif);
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const item = _mockKullanimKuraliKatalog.find((k) => k.kullanimKuraliId === Number(id));
    if (!item) throw _mockError(404, 'Kullanım kuralı bulunamadı.');
    if (('ad' in payload && !payload.ad) || ('beyanSorusu' in payload && !payload.beyanSorusu)) {
      throw _mockError(400, 'Ad ve beyan sorusu boş bırakılamaz.');
    }
    Object.assign(item, payload);
    return {
      ...item,
      fuarSayisi: Object.entries(_mockKullanimKurali).filter(([fid, rows]) => Number(fid) !== 0 && rows && rows[item.kod]).length,
    };
  }
  try {
    const res = await axios.put(`${BASE}/kullanim-kurallari/${id}`, payload, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. STAND TİPİ KURALLARI (fuar_stand_tipi_kural)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fuarın yazdığı (fuar, stand tipi) kuralları — sadece AÇIKÇA kaydedilmiş satırlar.
 * Bir tipin gerçekte hangi değerlerle yargılanacağı miras sonrasıdır: getEffectiveKural.
 * GET /fuar/{fuarId}/kural/stand-tipi
 */
export async function getStandTipiKurallari(fuarId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const map = _mockStandTipiKural[fuarId] ?? {};
    return Object.entries(map).map(([standTypeId, v]) => ({
      fuarId: Number(fuarId),
      standTypeId: Number(standTypeId),
      standTypeName: _mockStandTypeNames[standTypeId] ?? `#${standTypeId}`,
      ...v,
    }));
  }
  try {
    const res = await axios.get(`${BASE}/${fuarId}/kural/stand-tipi`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * Stand tipi kuralının bir alanının hangi seviyeden geldiği (#55/#57).
 * null = hiçbir yerde tanımlı değil.
 */
export const STAND_TIPI_KURAL_LEVEL = { FUAR: 'FUAR', GLOBAL: 'GLOBAL' };
export const STAND_TIPI_KURAL_LEVEL_LABELS = { FUAR: 'Bu fuar', GLOBAL: 'Global' };

/**
 * Tek alanın çözümü — backend resolver'ının aynası: ÖNCE fuarın satırları zincir boyunca
 * (tip → üst → kök), alanı hiçbiri doldurmuyorsa fuar 0'ın satırları aynı zincirde.
 * `chain` = [{ id, name }] tipten köke. Fuar 0 için yalnızca global katman var.
 */
function _resolveTypeField(chain, fuarRows, globalRows, field, isGlobal) {
  const layers = isGlobal
    ? [[STAND_TIPI_KURAL_LEVEL.GLOBAL, globalRows]]
    : [[STAND_TIPI_KURAL_LEVEL.FUAR, fuarRows], [STAND_TIPI_KURAL_LEVEL.GLOBAL, globalRows]];
  for (const [level, rows] of layers) {
    for (const node of chain) {
      const v = rows?.[node.id]?.[field];
      if (v != null) return { value: v, level, fromStandTypeId: node.id, fromStandTypeName: node.name ?? null };
    }
  }
  return { value: null, level: null, fromStandTypeId: null, fromStandTypeName: null };
}

/**
 * Stand tipi listesinden + fuar satırlarından + global (fuar 0) satırlarından ETKİN ağacı
 * kurar — `GET /kural/stand-tipi/effective` ile aynı şekil, derinlik öncelikli sırada.
 * Mock dalı ve istenirse ekran önizlemesi için.
 *
 * @param standTypes  düz liste — { stand_type_id, stand_type_name, parent_id } (projectService)
 *                    ya da { standTypeId, standTypeName, parentId }
 * @param fuarRows    { [standTypeId]: { yasak, minM2, maxM2, hMax } } ya da satır dizisi
 * @param globalRows  aynı şekil, fuar 0'ın satırları
 */
export function buildStandTipiEffectiveTree(standTypes, fuarRows, globalRows, { fuarId = null } = {}) {
  const isGlobal = Number(fuarId) === GLOBAL_KURAL_FUAR_ID;
  const toMap = (rows) => (Array.isArray(rows)
    ? Object.fromEntries(rows.map((r) => [Number(r.standTypeId), r]))
    : rows || {});
  const own = isGlobal ? {} : toMap(fuarRows);
  const glob = toMap(globalRows);

  const nodes = (standTypes || [])
    .map((t) => {
      const pid = t.parentId ?? t.parent_id ?? null;
      return {
        id: Number(t.standTypeId ?? t.stand_type_id),
        name: t.standTypeName ?? t.stand_type_name ?? null,
        parentId: pid == null ? null : Number(pid),
      };
    })
    .filter((n) => Number.isFinite(n.id));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const children = new Map();
  nodes.forEach((n) => {
    // Üstü listede olmayan tip kök sayılır — ağaçtan sessizce düşmesin.
    const key = n.parentId != null && byId.has(n.parentId) ? n.parentId : null;
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(n);
  });

  const ownRow = (r) => (r ? {
    yasak: r.yasak ?? null, minM2: r.minM2 ?? null, maxM2: r.maxM2 ?? null, hMax: r.hMax ?? null,
  } : null);

  const out = [];
  const seen = new Set();
  const visit = (n, depth) => {
    if (seen.has(n.id)) return;
    seen.add(n.id);
    const chain = [];
    const guard = new Set();
    let cur = n;
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      chain.push(cur);
      cur = cur.parentId != null ? byId.get(cur.parentId) : null;
    }
    const pick = (field) => _resolveTypeField(chain, own, glob, field, isGlobal);
    const yasak = pick('yasak');
    const minM2 = pick('minM2');
    const maxM2 = pick('maxM2');
    const hMax = pick('hMax');
    out.push({
      standTypeId: n.id,
      standTypeName: n.name,
      parentId: n.parentId,
      depth,
      fuarKural: isGlobal ? null : ownRow(own[n.id]),
      globalKural: ownRow(glob[n.id]),
      // Seviye, çözülen değer false ("açıkça izinli") olsa da doldurulur — ekran bunu
      // "İzinli · Bu fuar" diye gösterebilsin.
      forbidden: yasak.value === true,
      forbiddenLevel: yasak.level,
      forbiddenFromStandTypeId: yasak.fromStandTypeId,
      forbiddenFromStandTypeName: yasak.fromStandTypeName,
      minM2: minM2.value,
      minM2Level: minM2.level,
      minM2FromStandTypeId: minM2.fromStandTypeId,
      minM2FromStandTypeName: minM2.fromStandTypeName,
      maxM2: maxM2.value,
      maxM2Level: maxM2.level,
      maxM2FromStandTypeId: maxM2.fromStandTypeId,
      maxM2FromStandTypeName: maxM2.fromStandTypeName,
      hMax: hMax.value,
      hMaxLevel: hMax.level,
      hMaxFromStandTypeId: hMax.fromStandTypeId,
      hMaxFromStandTypeName: hMax.fromStandTypeName,
    });
    (children.get(n.id) || []).forEach((c) => visit(c, depth + 1));
  };
  (children.get(null) || []).forEach((r) => visit(r, 0));
  return out;
}

/**
 * Düzenleme modalının "devralınan değer" ipucu: `tree` (getStandTipiKuralEffective çıktısı)
 * üzerinde, düzenlenen katmanın BU TİPTEKİ kendi satırı yokmuş gibi çözer — yani alanı
 * boşaltırsanız (null) tipin alacağı değer. Düzenlenen katman: fuar sayfasında fuar
 * satırları, fuar 0'da global satırlar.
 * Dönüş: { value, level, fromStandTypeId, fromStandTypeName }
 */
export function resolveInheritedStandTypeField(tree, standTypeId, field, { fuarId = null } = {}) {
  const isGlobal = Number(fuarId) === GLOBAL_KURAL_FUAR_ID;
  const selfId = Number(standTypeId);
  const byId = new Map((tree || []).map((n) => [Number(n.standTypeId), n]));
  const chain = [];
  const guard = new Set();
  let cur = byId.get(selfId);
  while (cur && !guard.has(Number(cur.standTypeId))) {
    guard.add(Number(cur.standTypeId));
    chain.push({ id: Number(cur.standTypeId), name: cur.standTypeName ?? null });
    cur = cur.parentId != null ? byId.get(Number(cur.parentId)) : null;
  }
  const fuarRows = {};
  const globalRows = {};
  (tree || []).forEach((n) => {
    const id = Number(n.standTypeId);
    if (n.fuarKural && !(id === selfId && !isGlobal)) fuarRows[id] = n.fuarKural;
    if (n.globalKural && !(id === selfId && isGlobal)) globalRows[id] = n.globalKural;
  });
  return _resolveTypeField(chain, fuarRows, globalRows, field, isGlobal);
}

/**
 * Stand tipi ağacının tamamı, düz liste (derinlik öncelikli) — `getStandTypes` seviye seviye
 * yürünerek. Kök okuması patlarsa hata yükselir; bir alt dalın patlaması yalnızca o dalı düşürür.
 */
export async function loadStandTypeList({ forceLive = false, maxDepth = 8 } = {}) {
  async function walk(parentId, depth) {
    if (depth >= maxDepth) return [];
    let rows;
    if (depth === 0) rows = await getStandTypes(null, { forceLive });
    else rows = await getStandTypes(parentId, { forceLive }).catch(() => []);
    const list = Array.isArray(rows) ? rows : [];
    const nested = await Promise.all(list.map((t) => walk(t.stand_type_id, depth + 1)));
    return list.flatMap((t, i) => [{ ...t, parent_id: t.parent_id ?? parentId }, ...nested[i]]);
  }
  return walk(null, 0);
}

/**
 * ⭐ Stand Tipi Kuralları sekmesinin kaynağı (#55/#57): ağaçtaki her tip için ETKİN değerler,
 * her değerin seviyesi (FUAR | GLOBAL | null) ve hangi tipten geldiği; ayrıca o tipte fuarın
 * ve globalin KENDİ satırı (`fuarKural` / `globalKural`, yoksa null).
 * Fuar 0 için her şey GLOBAL çözülür ve fuarKural hep null.
 *
 * Mock dalı ağacı istemci tarafında kurar: `standTypes` verilirse onu, yoksa
 * loadStandTypeList() ile mock tip listesini kullanır.
 * GET /fuar/{fuarId}/kural/stand-tipi/effective
 */
export async function getStandTipiKuralEffective(fuarId, { forceLive = false, standTypes = null } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const types = standTypes ?? await loadStandTypeList({ forceLive: false });
    return buildStandTipiEffectiveTree(
      types,
      _mockStandTipiKural[fuarId] ?? {},
      _mockStandTipiKural[GLOBAL_KURAL_FUAR_ID] ?? {},
      { fuarId }
    );
  }
  try {
    const res = await axios.get(`${BASE}/${fuarId}/kural/stand-tipi/effective`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * Bir (fuar, stand tipi) kuralını oluştur/güncelle — o satırın TAM DEĞİŞTİRMESİ.
 * Dört alan da nullable; null = devral (önce üst tipler, sonra global):
 *   yasak: null miras al · true yasak · false AÇIKÇA İZİNLİ (üstteki yasağı kaldırır)
 * 400 → bilinmeyen stand tipi · minM2/maxM2/hMax <= 0 (temizlemek için null, 0 değil)
 *     · minM2 > maxM2 (hiçbir standın sağlayamayacağı kural sonradan her beyanı
 *       reddetmesin diye burada yakalanıyor).
 * PUT /fuar/{fuarId}/kural/stand-tipi/{standTypeId}
 */
export async function saveStandTipiKural(fuarId, standTypeId, body, { forceLive = false } = {}) {
  // '' (kullanıcı boş bıraktı) → null; false ile karıştırılmamalı.
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const payload = {
    yasak: body.yasak === '' || body.yasak == null ? null : Boolean(body.yasak),
    minM2: num(body.minM2),
    maxM2: num(body.maxM2),
    hMax: num(body.hMax),
  };
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    if (!_mockStandTipiKural[fuarId]) _mockStandTipiKural[fuarId] = {};
    _mockStandTipiKural[fuarId][standTypeId] = payload;
    return { fuarId: Number(fuarId), standTypeId: Number(standTypeId), ...payload };
  }
  try {
    const res = await axios.put(`${BASE}/${fuarId}/kural/stand-tipi/${standTypeId}`, payload,
      { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { parseError(e); }
}

/**
 * Kuralı sil — tip artık atalarından ve globalden miras alır. Her zaman 200.
 * ⚠️ belge_kural'ın aksine bu SERT bir silme: tamamı null olan bir satır ile hiç
 *    satır olmaması çözücü için aynı cevap, biri saklamak çöp olurdu.
 * DELETE /fuar/{fuarId}/kural/stand-tipi/{standTypeId}
 */
export async function deleteStandTipiKural(fuarId, standTypeId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    delete _mockStandTipiKural[fuarId]?.[standTypeId];
    return { deleted: true };
  }
  try {
    const res = await axios.delete(`${BASE}/${fuarId}/kural/stand-tipi/${standTypeId}`,
      { headers: getAuthHeader() });
    // DELETE'in `data`sı boş — sonucu `message` söyler, bu yüzden zarfı olduğu gibi veriyoruz.
    return res.data;
  } catch (e) { parseError(e); }
}

/**
 * ⭐ Beyan formunun İLK çağrısı. Bir (fuar, stand, stand tipi) üçlüsü için
 * çözülmüş kural setini + her değerin HANGİ SEVİYEDEN geldiğini döner.
 *
 * Bunun sayesinde form "maksimum 4,00 m" yerine "maksimum 4,00 m — Ahşap standlar
 * için" diyebiliyor, yasaklı tipi dropdown'da kapatabiliyor ve askGlass false iken
 * cam sorusunu hiç göstermiyor.
 *
 * ⚠️ `kimlikDogrulamaZorunlu` BURADA YOK ve olmamalı — o insanlarla ilgili bir ayar,
 *    bu formla değil. (2026-09-17'den beri zaten her fuarda zorunlu.)
 *
 * 2026-09-17 ek alanlar (her zaman dolu döner):
 *   kullanimKurallari: [{ kod, ad, beyanSorusu, yasakMesaji, izinli, sorulsun }] (aktifler; varsayılan [])
 *   serbestKwBlokKw / serbestKwBlokM2 (serbestKwMode "BLOCK" olabilir)
 *   forbiddenLevel / minM2Level / maxM2Level / heightLimitFromLevel: "FUAR" | "GLOBAL" | null
 * askGlass/allowGlass/askPaint/allowPaint uyumluluk için duruyor (CAM/BOYA'dan türetilir).
 * GET /fuar/{fuarId}/kural/effective?standId=&standTypeId=
 */
export async function getEffectiveKural(fuarId, { standId = null, standTypeId = null, forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const kural = await getFuarKural(fuarId);
    // Mock ağacı _mockStandTypeParents'tan yürünür (Ahşap/Metal/Karma → Özel Stand). Alan alan,
    // önce fuarın satırları zincir boyunca, sonra globalinkiler (canlı resolver ile aynı kural).
    const chain = [];
    const guard = new Set();
    let cur = standTypeId == null || standTypeId === '' ? null : Number(standTypeId);
    while (cur != null && !guard.has(cur)) {
      guard.add(cur);
      chain.push({ id: cur, name: _mockStandTypeNames[cur] ?? null });
      cur = _mockStandTypeParents[cur] ?? null;
    }
    const isGlobal = Number(fuarId) === GLOBAL_KURAL_FUAR_ID;
    const pick = (field) => _resolveTypeField(
      chain,
      isGlobal ? {} : (_mockStandTipiKural[fuarId] ?? {}),
      _mockStandTipiKural[GLOBAL_KURAL_FUAR_ID] ?? {},
      field,
      isGlobal
    );
    const forbidden = pick('yasak');
    const minM2 = pick('minM2');
    const maxM2 = pick('maxM2');
    const typeH = pick('hMax');
    const hm = await getEffectiveHMax(fuarId, { standId });
    // İki eksenin DÜŞÜĞÜ uygulanır — konum fiziksel, tip politika.
    // Tip sınırı konum sınırına EŞİTSE konum kaynağı yerinde kalır (guide tablosu).
    const candidates = [
      { v: hm.hMax, source: hm.source, name: null, level: null },
      { v: typeH.value, source: HMAX_SOURCE.STAND_TYPE, name: typeH.fromStandTypeName, level: typeH.level },
    ].filter((c) => c.v != null);
    const winner = candidates.length ? candidates.reduce((a, b) => (b.v < a.v ? b : a)) : null;
    return {
      fuarId: Number(fuarId), standId: standId ?? null,
      standTypeId: standTypeId == null ? null : Number(standTypeId),
      standTypeName: _mockStandTypeNames[standTypeId] ?? null,
      forbidden: forbidden.value === true,
      forbiddenLevel: forbidden.value === true ? forbidden.level : null,
      forbiddenFromStandTypeId: forbidden.value === true ? forbidden.fromStandTypeId : null,
      forbiddenFromStandTypeName: forbidden.value === true ? forbidden.fromStandTypeName : null,
      minM2: minM2.value, minM2Level: minM2.level,
      minM2FromStandTypeId: minM2.fromStandTypeId, minM2FromStandTypeName: minM2.fromStandTypeName,
      maxM2: maxM2.value, maxM2Level: maxM2.level,
      maxM2FromStandTypeId: maxM2.fromStandTypeId, maxM2FromStandTypeName: maxM2.fromStandTypeName,
      heightLimit: winner?.v ?? null,
      heightLimitSource: winner?.source ?? HMAX_SOURCE.NONE,
      heightLimitFromStandTypeName: winner?.source === HMAX_SOURCE.STAND_TYPE ? winner.name : null,
      heightLimitFromLevel: winner?.source === HMAX_SOURCE.STAND_TYPE ? winner.level : null,
      hallHMax: hm.hallHMax, hallName: hm.hallName,
      ceilingHeight: hm.ceilingHeight ?? null, fuarAlaniName: hm.fuarAlaniName ?? null,
      // 2026-09-03 ile gelen üç alan: dış politika çifti + çatı kırpması. Tip sınırı
      // kazandığında bile clampedByCeiling, YENDİĞİ konum sayısının çatıyla kesilip
      // kesilmediğini bildirir — ikisi bağımsız olgular.
      // 2026-09-15: fuarAlaniHMax kaldırıldı; kat tipi, salonun iki limiti ve varsayılanı geldi.
      fuarHMax: hm.fuarHMax ?? null,
      katTipi: hm.katTipi ?? null,
      hallHMaxTekKat: hm.hallHMaxTekKat ?? null, hallHMaxCiftKat: hm.hallHMaxCiftKat ?? null,
      allowedHMax: hm.allowedHMax ?? null,
      clampedByCeiling: hm.clampedByCeiling ?? false,
      clampedByDefault: hm.clampedByDefault ?? false,
      standTypeHMax: typeH.value,
      askGlass: kural.camSorulsun, allowGlass: kural.camIzinli,
      askPaint: kural.boyaSorulsun, allowPaint: kural.boyaIzinli,
      m2Required: kural.m2Zorunlu,
      serbestKwMode: kural.serbestKwMode,
      serbestKw: kural.serbestKw,
      serbestKwBlokKw: kural.serbestKwBlokKw ?? null,
      serbestKwBlokM2: kural.serbestKwBlokM2 ?? null,
      serbestKwKademeleri: kural.serbestKwKademeleri ?? [],
      kullanimKurallari: (kural.kullanimKurallari ?? []).map((r) => ({
        kod: r.kod, ad: r.ad, beyanSorusu: r.beyanSorusu, yasakMesaji: r.yasakMesaji ?? null,
        izinli: r.izinli, sorulsun: r.sorulsun,
      })),
      kuralConfigured: kural.configured !== false,
    };
  }
  try {
    const params = {};
    if (standId != null) params.standId = standId;
    if (standTypeId != null) params.standTypeId = standTypeId;
    const res = await axios.get(`${BASE}/${fuarId}/kural/effective`, { params, headers: getAuthHeader() });
    const data = res.data ?? {};
    // Yeni alanlar her zaman tanımlı dönsün — beyan formu `.map` / `== null` ile okuyor.
    return {
      ...data,
      kullanimKurallari: Array.isArray(data.kullanimKurallari) ? data.kullanimKurallari : [],
      serbestKwBlokKw: data.serbestKwBlokKw ?? null,
      serbestKwBlokM2: data.serbestKwBlokM2 ?? null,
    };
  } catch (e) { parseError(e); }
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. GLOBAL VARSAYILANLAR — okuma/yazma sarmalayıcıları + kopyalama
// ─────────────────────────────────────────────────────────────────────────────
// Hepsi yukarıdaki normal uçların GLOBAL_KURAL_FUAR_ID ile çağrılmış hâli. Ayrı
// isimlerle duruyorlar ki çağıran taraf "burada global düzenliyorum" derken bunu
// yazıyor olsun — fuar sayfasında yanlışlıkla global yazılmasının önündeki tek
// gerçek engel budur.

export const getGlobalKural = (opts = {}) => getFuarKural(GLOBAL_KURAL_FUAR_ID, opts);
export const saveGlobalKural = (body, opts = {}) => saveFuarKural(GLOBAL_KURAL_FUAR_ID, body, opts);
export const getGlobalSerbestKwBands = (opts = {}) => getSerbestKwBands(GLOBAL_KURAL_FUAR_ID, opts);
export const saveGlobalSerbestKwBands = (bands, opts = {}) => saveSerbestKwBands(GLOBAL_KURAL_FUAR_ID, bands, opts);
export const getGlobalStandTipiKurallari = (opts = {}) => getStandTipiKurallari(GLOBAL_KURAL_FUAR_ID, opts);
export const saveGlobalStandTipiKural = (standTypeId, body, opts = {}) =>
  saveStandTipiKural(GLOBAL_KURAL_FUAR_ID, standTypeId, body, opts);
export const deleteGlobalStandTipiKural = (standTypeId, opts = {}) =>
  deleteStandTipiKural(GLOBAL_KURAL_FUAR_ID, standTypeId, opts);
export const getGlobalStandTipiKuralEffective = (opts = {}) =>
  getStandTipiKuralEffective(GLOBAL_KURAL_FUAR_ID, opts);

/**
 * Serbest kW + kural setini bir fuara DOĞRU SIRAYLA yazar (sayfalar ve kopyalama ortak).
 *   · Hedef BANDS: önce PUT /kural (serbestKw ve blok çifti null), SONRA PUT kademeler —
 *     kademe PUT'u sabit/blok doluyken 400.
 *   · Diğer modlar: önce PUT kademeler [], SONRA PUT /kural — kural PUT'u kademe varken 400.
 * `body` saveFuarKural gövdesi; mod dışı alanlar burada null'lanır.
 */
export async function saveFuarKuralWithMode(fuarId, mode, body, bands, { forceLive = false } = {}) {
  const opts = { forceLive };
  const scalar = {
    ...body,
    serbestKw: mode === SERBEST_KW_MODE.FLAT ? body.serbestKw : null,
    serbestKwBlokKw: mode === SERBEST_KW_MODE.BLOCK ? body.serbestKwBlokKw : null,
    serbestKwBlokM2: mode === SERBEST_KW_MODE.BLOCK ? body.serbestKwBlokM2 : null,
  };
  if (mode === SERBEST_KW_MODE.BANDS) {
    await saveFuarKural(fuarId, scalar, opts);
    await saveSerbestKwBands(fuarId, bands || [], opts);
  } else {
    await saveSerbestKwBands(fuarId, [], opts);
    await saveFuarKural(fuarId, scalar, opts);
  }
}
export const saveGlobalKuralWithMode = (mode, body, bands, opts = {}) =>
  saveFuarKuralWithMode(GLOBAL_KURAL_FUAR_ID, mode, body, bands, opts);

/**
 * Global şablonu bir fuara KOPYALAR — yeni fuar oluşturulduğunda otomatik, sonradan
 * elle de çağrılabilir ("Global şablonu uygula").
 *
 * ⚠️ KOPYADIR, MİRAS DEĞİL. Kopyalandıktan sonra iki taraf birbirinden bağımsızdır:
 *    globali değiştirmek eski fuarları etkilemez, fuarı değiştirmek globali bozmaz.
 *    Kopyalananlar: skaler kurallar (serbest kW modu dahil), kW kademeleri, kullanım kuralları.
 *
 * ⚠️ 2026-09-17 (#55/#57): STAND TİPİ KURALLARI ARTIK KOPYALANMAZ. Fuarlar onları fuar 0'dan
 *    canlı miras alıyor; kopyalamak bugünkü global değerleri fuar üzerinde "geçersiz kılma"
 *    olarak dondurur ve globaldeki sonraki değişiklikler o fuara hiç ulaşmazdı.
 *
 * @param {number}  fuarId
 * @param {object}  opts
 * @param {boolean} opts.overwrite  false (varsayılan) ise fuarın KENDİ kural seti
 *                                  zaten varsa (configured) hiçbir şey yapmaz —
 *                                  yapılandırılmış bir fuarı ezmemek için.
 * @returns {{applied: boolean, reason?: string, kural?: object, bands?: number, kullanimKurallari?: number}}
 */
export async function applyGlobalDefaultsToFuar(fuarId, { overwrite = false, forceLive = false } = {}) {
  if (fuarId == null || Number(fuarId) === GLOBAL_KURAL_FUAR_ID) {
    return { applied: false, reason: i18n.t("services:fuarKuralService.gecersizFuar") };
  }
  const [global, globalBands, current] = await Promise.all([
    getGlobalKural({ forceLive }),
    getGlobalSerbestKwBands({ forceLive }),
    getFuarKural(fuarId, { forceLive }),
  ]);

  if (!overwrite && current?.configured === true) {
    return { applied: false, reason: i18n.t("services:fuarKuralService.buFuarinKendiKuralSeti") };
  }
  const globalUsage = Array.isArray(global?.kullanimKurallari) ? global.kullanimKurallari : [];
  if (global?.configured === false && globalBands.length === 0 && !globalUsage.some((r) => r.configured)) {
    return { applied: false, reason: i18n.t("services:fuarKuralService.globalVarsayilanTanimlanmamisKopyalanacakBir") };
  }

  // Mod GET'ten; gelmezse veriden (kademeler ayrı okunduğu için onları da katıyoruz).
  const globalMode = global?.serbestKwMode
    ?? deriveSerbestKwMode({ ...global, serbestKwKademeleri: globalBands });
  await saveFuarKuralWithMode(
    fuarId,
    globalMode,
    { ...global, kullanimKurallari: globalUsage },
    globalBands,
    { forceLive }
  );

  return {
    applied: true,
    kural: await getFuarKural(fuarId, { forceLive }),
    bands: globalMode === SERBEST_KW_MODE.BANDS ? globalBands.length : 0,
    kullanimKurallari: globalUsage.length,
  };
}

// ─── Named rule-set templates (backlog #88/#89) ────────────────────────────────
// Şablon olarak kaydet / Şablon yükle — save the CURRENT set under a name, load a named set into
// any fair later. KOPYADIR, applyGlobalDefaultsToFuar ile aynı anlamda: yükleme fuarın o kapsamdaki
// mevcut değerlerinin üzerine yazar, sonrasında ikisi bağımsızdır. kapsam: 1 = Proje Kural Seti
// (fuar_kural + serbest_kw + kullanım kuralları), 2 = Stand Tipi Kuralları.

export const KURAL_SETI_SABLON_KAPSAM = { PROJE_KURAL_SETI: 1, STAND_TIPI_KURALI: 2 };

// Mock mode has no real fair data to snapshot, so a saved template just remembers its name/kapsam —
// enough to exercise the save/load/list UI flow without a backend.
let _mockSablonNextId = 1;
const _mockSablonlar = [];

/** Every saved template for one kapsam, name-sorted. GET /fuar/kural-seti-sablon?kapsam= */
export async function listKuralSetiSablonlar(kapsam, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return _mockSablonlar
      .filter((s) => s.kapsam === kapsam)
      .sort((a, b) => a.ad.localeCompare(b.ad, 'tr'));
  }
  try {
    const res = await axios.get(`${BASE}/kural-seti-sablon`, {
      params: { kapsam }, headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { parseError(e); }
}

/**
 * Snapshots fuarId's CURRENT state for kapsam into a new named template.
 * POST /fuar/{fuarId}/kural-seti-sablon
 */
export async function saveKuralSetiSablon(fuarId, kapsam, ad, { forceLive = false } = {}) {
  const name = (ad || '').trim();
  if (!name) throw _mockError(400, i18n.t("services:fuarKuralService.sablonAdiZorunludur"));
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const created = { sablonId: _mockSablonNextId++, ad: name, kapsam, createdAt: new Date().toISOString() };
    _mockSablonlar.push(created);
    return created;
  }
  try {
    const res = await axios.post(`${BASE}/${fuarId}/kural-seti-sablon`, { ad: name, kapsam },
      { headers: getAuthHeader() });
    if (res.data?.result === false) throw _mockError(400, res.data.message);
    return res.data?.data ?? res.data;
  } catch (e) { parseError(e); }
}

/**
 * Loads (copies) a saved template into fuarId, OVERWRITING whatever it already has for that
 * template's scope. Caller must confirm first — this is a full-replace write.
 * POST /fuar/{fuarId}/kural-seti-sablon/{sablonId}/load
 */
export async function loadKuralSetiSablon(fuarId, sablonId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const found = _mockSablonlar.find((s) => s.sablonId === sablonId);
    if (!found) throw _mockError(400, 'Şablon bulunamadı.');
    return { message: `'${found.ad}' şablonu bu fuara kopyalandı.` };
  }
  try {
    const res = await axios.post(`${BASE}/${fuarId}/kural-seti-sablon/${sablonId}/load`, {},
      { headers: getAuthHeader() });
    if (res.data?.result === false) throw _mockError(400, res.data.message);
    return res.data;
  } catch (e) { parseError(e); }
}

/** Soft-deletes a template. DELETE /fuar/kural-seti-sablon/{sablonId} */
export async function deleteKuralSetiSablon(sablonId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const idx = _mockSablonlar.findIndex((s) => s.sablonId === sablonId);
    if (idx >= 0) _mockSablonlar.splice(idx, 1);
    return { message: 'Şablon silindi.' };
  }
  try {
    const res = await axios.delete(`${BASE}/kural-seti-sablon/${sablonId}`, { headers: getAuthHeader() });
    if (res.data?.result === false) throw _mockError(400, res.data.message);
    return res.data;
  } catch (e) { parseError(e); }
}
